/**
 * Caravan Calculator JavaScript
 * Converts Svelte calculator logic to vanilla JavaScript for Liquid theme
 */

// Patch getFocusableElements to handle null containers
if (typeof getFocusableElements !== 'undefined') {
  const originalGetFocusableElements = getFocusableElements;
  globalThis.getFocusableElements = function (container) {
    if (!container) {
      console.warn('getFocusableElements called with null container');
      return [];
    }
    return originalGetFocusableElements(container);
  };
}

// Patch CartNotification to handle missing sections gracefully
// This will be applied after cart-notification.js loads
setTimeout(() => {
  const CartNotificationElement = customElements.get('cart-notification');
  if (CartNotificationElement) {
    const originalRenderContents = CartNotificationElement.prototype.renderContents;
    CartNotificationElement.prototype.renderContents = function (parsedState) {
      // Verify all required sections exist before rendering
      const requiredSections = this.getSectionsToRender().map((s) => s.id);
      const missingSections = requiredSections.filter((id) => !parsedState.sections?.[id]);

      if (missingSections.length > 0) {
        console.warn('CartNotification: Missing required sections:', missingSections);
        // Don't render if sections are missing - it will cause errors
        return;
      }

      return originalRenderContents.call(this, parsedState);
    };
  }
}, 100);

class CaravanCalculator {
  constructor(sectionId, config) {
    this.sectionId = sectionId;
    this.initializeConfigProperties(config);
    this.initializeState();
    this.initializeCoverageRates(config);
    this.bindMethods();
  }

  /**
   * Initialize configuration properties from config object
   */
  initializeConfigProperties(config) {
    this.products = config.products || [];
    // Handle discountRate properly - 0 is a valid value, so check for undefined/null
    this.discountRate = config.discountRate !== undefined && config.discountRate !== null ? config.discountRate : 0.1;
    this.storeDomain = config.storeDomain || '';
    this.calculatorId = config.calculatorId || '';
    this.inputMode = config.inputMode || 'length';

    // Initialize dimension defaults and limits
    this.defaultLength = this.parseConfigNumber(config.defaultLength, 6);
    this.defaultWidth = this.parseConfigNumber(config.defaultWidth, 2.5);
    this.defaultArea = this.parseConfigNumber(config.defaultArea, 15);
    this.maxLength = this.parseConfigNumber(config.maxLength, 30);
    this.maxWidth = this.parseConfigNumber(config.maxWidth, 10);
    this.maxArea = this.parseConfigNumber(config.maxArea, 300);
    this.minLength = this.parseConfigNumber(config.minLength, 0.5);
    this.minWidth = this.parseConfigNumber(config.minWidth, 0.5);
    this.minArea = this.parseConfigNumber(config.minArea, 0.5);

    // Initialize toggle settings
    this.showRawMetalToggle = config.showRawMetalToggle ?? true;
    this.showEtchPrimer = config.showEtchPrimer ?? false;
    this.showFullCoverToggle = config.showFullCoverToggle ?? false;
    this.showConcreteRoofToggle = config.showConcreteRoofToggle ?? false;
    this.fullCoverToggleLabel = config.fullCoverToggleLabel || 'Full cover?';
    this.fullCoverTooltipText = config.fullCoverTooltipText || 'Full coverage vs seams only';

    // Initialize bonus kit configuration
    this.bonusKitAreaThreshold = config.bonusKitAreaThreshold ?? 5;
    this.bonusBrushProductHandle = config.bonusBrushProductHandle || null;
    this.bonusBrushRollerProductHandle = config.bonusBrushRollerProductHandle || null;
    this.bonusBrushVariantId = config.bonusBrushVariantId || null;
    this.bonusBrushRollerVariantId = config.bonusBrushRollerVariantId || null;
    this.bonusBrushText = config.bonusBrushText || 'Brush Kit';
    this.bonusBrushPrice = config.bonusBrushPrice || '14.99';
    this.bonusBrushRollerText = config.bonusBrushRollerText || 'Brush + Roller Kit';
    this.bonusBrushRollerPrice = config.bonusBrushRollerPrice || '39.99';
  }

  /**
   * Parse a config number value with fallback to default
   */
  parseConfigNumber(value, defaultValue) {
    return value !== undefined && value !== null ? Number.parseFloat(value) : defaultValue;
  }

  /**
   * Initialize calculator state
   */
  initializeState() {
    this.length = this.defaultLength;
    this.width = this.defaultWidth;
    this.area = this.defaultArea;
    // If showEtchPrimer is true, default to bare-metal (etch primer visible)
    this.roofType = this.showEtchPrimer ? 'bare-metal' : 'painted-roof';
    this.fullCover = false; // Default to seams only (not full coverage)
    this.concreteRoof = false; // Default to off
    this.selectedColors = {};
    this.items = [];
    this.breakdowns = [];
    this.totalItems = 0;
    this.originalPrice = 0;
    this.discountedPrice = 0;
    this.savings = 0;
    this.lastCalculatedArea = null;
    this.manuallyAdjustedBuckets = new Set(); // Track buckets that were manually adjusted by user
  }

  /**
   * Initialize coverage rates with defaults and merge custom rates
   */
  initializeCoverageRates(config) {
    // Coverage rates (litres per m²) - merge custom rates with defaults
    // All rates use multiplication: litres = area * rate
    // Using 1000 factor to preserve 3 decimal places for products like sealer (0.125)
    const defaultCoverageRates = {
      'waterproof-sealant': (area, fullCover = true) => Math.round((fullCover ? area * 1.5 : area * 0.2) * 1000) / 1000,
      'protective-top-coat': (area) => Math.round(area * 0.5 * 1000) / 1000,
      sealer: (area) => Math.round(area * 0.125 * 1000) / 1000, // 0.125 L/m² (was area / 8)
      'etch-primer': (area) => Math.round(area * (1 / 6) * 1000) / 1000, // ~0.167 L/m² (was area / 6)
      // rapid-cure-spray is calculated separately based on sealant litres, not area
      // This 0 return is a safeguard - it should never be called through calculateBuckets()
      'rapid-cure-spray': () => 0,
      'geo-textile': () => 0, // Calculated via calculateGeoTextile() based on area multiplier (sealant litres)
      'seam-tape': () => 0, // Not calculated via coverage rates
      'brush-roller': () => 0, // Calculated via calculateVariants() based on area threshold
    };
    this.coverageRates = config.coverageRates
      ? { ...defaultCoverageRates, ...config.coverageRates }
      : defaultCoverageRates;
  }

  /**
   * Bind methods to preserve context
   */
  bindMethods() {
    this.init = this.init.bind(this);
    this.handleLengthInput = this.handleLengthInput.bind(this);
    this.handleRoofTypeToggle = this.handleRoofTypeToggle.bind(this);
    this.handleFullCoverToggle = this.handleFullCoverToggle.bind(this);
    this.handleConcreteRoofToggle = this.handleConcreteRoofToggle.bind(this);
    this.updateCalculations = this.updateCalculations.bind(this);
  }

  // Helper to normalize product handles (extract product type from full Shopify handle)
  normalizeHandle(handle) {
    if (!handle) return handle;
    // Extract the product type from handles like "liquid-rubber-diy-waterproof-sealant"
    if (handle.includes('waterproof-sealant')) return 'waterproof-sealant';
    if (handle.includes('protective-top-coat') || handle.includes('thermal-coating')) return 'protective-top-coat';
    if (handle.includes('tough-coat') || handle.includes('tough_coat')) return 'tough-coat';
    if (handle.includes('sealer') && !handle.includes('etch')) return 'sealer';
    if (handle.includes('etch-primer')) return 'etch-primer';
    if (handle.includes('rapid-cure-spray')) return 'rapid-cure-spray';
    if (handle.includes('geo-textile')) return 'geo-textile';
    if (handle.includes('seam-tape')) return 'seam-tape';
    if (handle.includes('brush') || handle.includes('roller')) return 'brush-roller';
    return handle;
  }

  // Helper to update swatch selection state (reduces nesting)
  updateSwatchSelectionState(swatch, swatchLabel, isSelected) {
    requestAnimationFrame(() => {
      if (isSelected) {
        swatch.classList.add('custom-color-swatch--selected');
        if (swatchLabel) {
          swatchLabel.classList.add('custom-color-swatch--selected');
        }
      } else {
        swatch.classList.remove('custom-color-swatch--selected');
        if (swatchLabel) {
          swatchLabel.classList.remove('custom-color-swatch--selected');
        }
      }
    });
  }

  // Helper to find visible input by color value (reduces nesting)
  findVisibleInputByColor(swatchesContainer, colorValue, variantId) {
    if (!colorValue) return null;
    // Find by matching color value
    const allVisibleInputs = swatchesContainer.querySelectorAll('input[type="radio"]');
    for (const vi of allVisibleInputs) {
      if (vi.value === colorValue) {
        return vi;
      }
    }
    // Fallback: try to find by variant ID
    return swatchesContainer.querySelector(`input[data-variant-id="${variantId}"]`);
  }

  // Helper to check input and uncheck others (reduces nesting)
  checkInputAndUncheckOthers(swatchesContainer, inputToCheck) {
    // Uncheck all other inputs first
    swatchesContainer.querySelectorAll('input[type="radio"]').forEach((inp) => {
      inp.checked = false;
    });
    inputToCheck.checked = true;
    inputToCheck.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /**
   * Get section ID and prefix, caching the result
   */
  getSectionIdentifiers() {
    if (!this._cachedSectionIdOnly) {
      this._cachedSectionIdOnly =
        this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    }
    if (!this._cachedSectionPrefix) {
      this._cachedSectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    }
    return {
      sectionIdOnly: this._cachedSectionIdOnly,
      sectionPrefix: this._cachedSectionPrefix,
    };
  }

  /**
   * Hide the loading state skeleton
   */
  hideLoadingState() {
    const { sectionIdOnly } = this.getSectionIdentifiers();
    try {
      const loadingState = document.getElementById(`calculator-loading-${sectionIdOnly}`);
      if (loadingState) {
        loadingState.style.display = 'none';
        return;
      }
    } catch (error) {
      console.error('Error hiding skeleton loader:', error);
    }
    // Fallback: try querySelector
    const loadingState = document.querySelector(`#calculator-loading-${sectionIdOnly}`);
    if (loadingState) {
      loadingState.style.display = 'none';
    }
  }

  /**
   * Initialize a toggle with default state
   */
  initializeToggle(toggleId, defaultChecked, stateProperty) {
    const { sectionIdOnly, sectionPrefix } = this.getSectionIdentifiers();
    const toggle = document.querySelector(`${sectionPrefix} #${toggleId}-${sectionIdOnly}`);
    if (toggle) {
      toggle.checked = defaultChecked;
      this[stateProperty] = defaultChecked;
    }
  }

  /**
   * Initialize all toggles based on configuration
   */
  initializeToggles() {
    // Initialize roofType toggle if showEtchPrimer is enabled
    if (this.showEtchPrimer && this.showRawMetalToggle) {
      this.initializeToggle('roof-type-toggle', true, 'roofType');
      this.roofType = 'bare-metal';
    }

    // Initialize fullCover toggle if enabled
    if (this.showFullCoverToggle) {
      this.initializeToggle('full-cover-toggle', false, 'fullCover');
    }

    // Initialize concreteRoof toggle if enabled
    if (this.showConcreteRoofToggle) {
      this.initializeToggle('concrete-roof-toggle', false, 'concreteRoof');
    }
  }

  init() {
    // Hide loading state first
    this.hideLoadingState();

    try {
      this.setupEventListeners();
      this.initializeProducts();
      this.initializeToggles();
      this.updateRoofTypeUI(
        this.calculatorId || this.sectionId.replace('shopify-section-', '').replace('#', '')
      );
      this.updateCalculations();
    } catch (error) {
      console.error('Error during calculator initialization:', error);
      // Ensure skeleton is hidden even if there's an error
      this.hideLoadingState();
      // Re-throw error for debugging
      throw error;
    }
  }

  setupEventListeners() {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly = this.calculatorId || this.sectionId.replace('shopify-section-', '').replace('#', '');

    this.setupLengthInput(sectionPrefix, sectionIdOnly);
    this.setupWidthInput(sectionPrefix, sectionIdOnly);
    this.setupAreaInput(sectionPrefix, sectionIdOnly);
    this.setupToggles(sectionPrefix, sectionIdOnly);
    this.setupToggleTooltips(sectionPrefix);
    this.setupActionButtons(sectionPrefix, sectionIdOnly);

    this.sectionIdOnly = sectionIdOnly;
  }

  setupLengthInput(sectionPrefix, sectionIdOnly) {
    const lengthInput = document.querySelector(`${sectionPrefix} #length-input-${sectionIdOnly}`);
    if (!lengthInput) return;

    // On focus, select full value so typing replaces it (not just appends).
    lengthInput.addEventListener('focus', (e) => {
      e.target.select();
    });
    // Also select all on click so when clicking into the field the full value is selected.
    lengthInput.addEventListener('click', (e) => {
      e.target.select();
    });

    // While typing: only sanitize input (no lock-in). Value locks in on blur.
    lengthInput.addEventListener('input', (e) => {
      this.handleLengthInput(e);
    });

    // Lock in value only when user leaves the field (click outside, tab, or Enter).
    lengthInput.addEventListener('blur', () => {
      const parsed = this.parseDecimal(lengthInput.value);
      if (parsed !== null && parsed >= this.minLength && parsed <= this.maxLength) {
        this.length = Math.round(parsed * 2) / 2;
        lengthInput.value = this.length.toFixed(1);
      } else {
        lengthInput.value = this.length.toFixed(1);
      }
      this.updateCalculations();
    });

    lengthInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        lengthInput.blur();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.adjustLength(0.5);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.adjustLength(-0.5);
      }
    });

    const decreaseBtn = document.querySelector(`${sectionPrefix} [data-action="decrease-length"]`);
    const increaseBtn = document.querySelector(`${sectionPrefix} [data-action="increase-length"]`);
    if (decreaseBtn) this.setupHoldButton(decreaseBtn, () => this.adjustLength(-0.5));
    if (increaseBtn) this.setupHoldButton(increaseBtn, () => this.adjustLength(0.5));
  }

  setupWidthInput(sectionPrefix, sectionIdOnly) {
    const widthInput = document.querySelector(`${sectionPrefix} #width-input-${sectionIdOnly}`);
    if (!widthInput) return;

    // On focus, select full value so typing replaces it (not just appends).
    widthInput.addEventListener('focus', (e) => {
      e.target.select();
    });
    // Also select all on click so when clicking into the field the full value is selected.
    widthInput.addEventListener('click', (e) => {
      e.target.select();
    });

    // While typing: only sanitize (strip non-numeric). Value locks in on blur.
    widthInput.addEventListener('input', (e) => {
      const value = e.target.value.replaceAll(/[^\d,.\s]/g, '').replaceAll(/\s+/g, '');
      e.target.value = value;
    });

    // Lock in value only when user leaves the field (click outside, tab, or Enter).
    widthInput.addEventListener('blur', () => {
      const parsed = this.parseDecimal(widthInput.value);
      if (parsed !== null && parsed >= this.minWidth && parsed <= this.maxWidth) {
        this.width = Math.round(parsed * 10) / 10;
        widthInput.value = this.width.toFixed(1);
      } else {
        widthInput.value = this.width.toFixed(1);
      }
      this.updateCalculations();
    });

    widthInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        widthInput.blur();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.adjustWidth(0.1);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.adjustWidth(-0.1);
      }
    });

    const decreaseBtn = document.querySelector(`${sectionPrefix} [data-action="decrease-width"]`);
    const increaseBtn = document.querySelector(`${sectionPrefix} [data-action="increase-width"]`);
    if (decreaseBtn) this.setupHoldButton(decreaseBtn, () => this.adjustWidth(-0.1));
    if (increaseBtn) this.setupHoldButton(increaseBtn, () => this.adjustWidth(0.1));
  }

  setupAreaInput(sectionPrefix, sectionIdOnly) {
    const areaInput = document.querySelector(`${sectionPrefix} #area-input-${sectionIdOnly}`);
    if (!areaInput) return;

    // On focus, select full value so typing replaces it (not just after decimal).
    areaInput.addEventListener('focus', (e) => {
      e.target.select();
    });
    // Also select all on click so when clicking into the field the full value is selected.
    areaInput.addEventListener('click', (e) => {
      e.target.select();
    });

    // While typing: only sanitize (strip non-numeric). Value locks in on blur.
    areaInput.addEventListener('input', (e) => {
      const value = e.target.value.replaceAll(/[^\d,.\s]/g, '').replaceAll(/\s+/g, '');
      e.target.value = value;
    });

    // Lock in value only when user leaves the field (click outside, tab, or Enter).
    areaInput.addEventListener('blur', () => {
      const parsed = this.parseDecimal(areaInput.value);
      if (parsed !== null && parsed >= this.minArea && parsed <= this.maxArea) {
        this.area = Math.round(parsed * 10) / 10;
        areaInput.value = this.area.toFixed(1);
      } else {
        areaInput.value = this.area.toFixed(1);
      }
      this.updateCalculations();
    });

    areaInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        areaInput.blur();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.adjustArea(0.5);
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        this.adjustArea(-0.5);
      }
    });

    const decreaseBtn = document.querySelector(`${sectionPrefix} [data-action="decrease-area"]`);
    const increaseBtn = document.querySelector(`${sectionPrefix} [data-action="increase-area"]`);
    if (decreaseBtn) this.setupHoldButton(decreaseBtn, () => this.adjustArea(-0.5));
    if (increaseBtn) this.setupHoldButton(increaseBtn, () => this.adjustArea(0.5));
  }

  setupToggles(sectionPrefix, sectionIdOnly) {
    const roofToggle = document.querySelector(`${sectionPrefix} #roof-type-toggle-${sectionIdOnly}`);
    if (roofToggle) {
      roofToggle.addEventListener('change', () => this.handleRoofTypeToggle());
    }

    const fullCoverToggle = document.querySelector(`${sectionPrefix} #full-cover-toggle-${sectionIdOnly}`);
    if (fullCoverToggle) {
      fullCoverToggle.addEventListener('change', () => this.handleFullCoverToggle());
    }

    const concreteRoofToggle = document.querySelector(`${sectionPrefix} #concrete-roof-toggle-${sectionIdOnly}`);
    if (concreteRoofToggle) {
      concreteRoofToggle.addEventListener('change', () => this.handleConcreteRoofToggle());
    }
  }

  setupToggleTooltips(sectionPrefix) {
    const toggleTooltipTriggers = document.querySelectorAll(`${sectionPrefix} .toggle-tooltip-trigger`);
    toggleTooltipTriggers.forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const tooltipContainer = trigger.closest('.toggle-tooltip-container');
        if (!tooltipContainer) return;

        const isActive = tooltipContainer.classList.contains('active');
        this.closeOtherTooltips(sectionPrefix, tooltipContainer);

        if (isActive) {
          tooltipContainer.classList.remove('active');
          trigger.setAttribute('aria-expanded', 'false');
        } else {
          tooltipContainer.classList.add('active');
          trigger.setAttribute('aria-expanded', 'true');
          this.adjustTooltipPositionOnMobile(tooltipContainer);
        }
      });
    });
  }

  closeOtherTooltips(sectionPrefix, currentContainer) {
    const allTooltipContainers = document.querySelectorAll(
      `${sectionPrefix} .toggle-tooltip-container, ${sectionPrefix} .product-why-tooltip-container`
    );
    allTooltipContainers.forEach((container) => {
      if (container !== currentContainer) {
        container.classList.remove('active');
      }
    });
  }

  adjustTooltipPositionOnMobile(tooltipContainer) {
    if (window.innerWidth > 749) return;

    const tooltip = tooltipContainer.querySelector('.toggle-tooltip');
    if (!tooltip) return;

    tooltipContainer.classList.remove('tooltip-left');

    requestAnimationFrame(() => {
      const tooltipRect = tooltip.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const padding = 16;
      const overflowsLeft = tooltipRect.left < padding;

      if (overflowsLeft) {
        tooltipContainer.classList.add('tooltip-left');
        requestAnimationFrame(() => {
          const newTooltipRect = tooltip.getBoundingClientRect();
          const overflowsRight = newTooltipRect.right > viewportWidth - padding;
          if (overflowsRight) {
            tooltipContainer.classList.remove('tooltip-left');
          }
        });
      } else {
        tooltipContainer.classList.remove('tooltip-left');
      }
    });
  }

  setupActionButtons(sectionPrefix, sectionIdOnly) {
    const resetBtn = document.querySelector(`${sectionPrefix} #reset-calculator-${sectionIdOnly}`);
    if (resetBtn) {
      resetBtn.addEventListener('click', () => this.resetToDefaults());
    }

    const closePopupBtn = document.querySelector(`${sectionPrefix} .close-popup`);
    if (closePopupBtn) {
      closePopupBtn.addEventListener('click', () => this.closePopup());
    }

    const ctaBtn = document.querySelector(`${sectionPrefix} #cta-caravan-${sectionIdOnly}`);
    if (ctaBtn) {
      ctaBtn.addEventListener('click', () => this.goToCheckout());
    }
  }

  setupHoldButton(button, callback) {
    let holdTimeout;
    let holdInterval;

    const startHold = () => {
      callback();
      holdTimeout = setTimeout(() => {
        holdInterval = setInterval(callback, 75);
      }, 300);
    };

    const clearHold = () => {
      clearTimeout(holdTimeout);
      clearInterval(holdInterval);
    };

    button.addEventListener('mousedown', startHold);
    button.addEventListener('mouseup', clearHold);
    button.addEventListener('mouseleave', clearHold);
    button.addEventListener('touchstart', startHold, { passive: true });
    button.addEventListener('touchend', clearHold);
  }

  handleLengthInput(e) {
    const value = e.target.value.replaceAll(/[^\d,.\s]/g, '').replaceAll(/\s+/g, '');
    e.target.value = value;
  }

  adjustLength(delta) {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const lengthInput = document.querySelector(`#length-input-${sectionIdOnly}`);
    if (!lengthInput) return;

    // Use this.length as source of truth instead of reading from input field
    // This ensures calculations are always based on the current value, not stale input values
    let current = this.length || this.minLength;
    current = Math.max(this.minLength, Math.min(this.maxLength, current + delta));
    current = Math.round(current * 2) / 2;

    this.length = current;
    lengthInput.value = current.toFixed(1);
    this.updateCalculations();
  }

  adjustWidth(delta) {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const widthInput = document.querySelector(`#width-input-${sectionIdOnly}`);
    if (!widthInput) return;

    let current = this.width || this.minWidth;
    current = Math.max(this.minWidth, Math.min(this.maxWidth, current + delta));
    current = Math.round(current * 10) / 10;

    this.width = current;
    widthInput.value = current.toFixed(1);
    this.updateCalculations();
  }

  adjustArea(delta) {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const areaInput = document.querySelector(`#area-input-${sectionIdOnly}`);
    if (!areaInput) return;

    let current = this.area || this.minArea;
    current = Math.max(this.minArea, Math.min(this.maxArea, current + delta));
    current = Math.round(current * 2) / 2; // Round to 0.5 increments

    this.area = current;
    areaInput.value = current.toFixed(1);
    this.updateCalculations();
  }

  parseDecimal(str) {
    if (!str) return null;
    let s = str.trim().replaceAll(/\s/g, '');
    if (s.includes(',') && s.includes('.')) {
      const lastComma = s.lastIndexOf(',');
      const lastDot = s.lastIndexOf('.');
      if (lastComma > lastDot) {
        s = s.replaceAll('.', '').replace(',', '.');
      } else {
        s = s.replaceAll(',', '');
      }
    } else {
      s = s.replace(',', '.');
    }
    s = s.replaceAll(/[^0-9.-]/g, '');
    if (s.startsWith('.')) s = '0' + s;
    const num = Number(s);
    return Number.isFinite(num) ? num : null;
  }

  /**
   * Swap the roof-type info tooltip text and active state label
   * between the Etch Primer (raw metal) and Sealer (painted) messages.
   */
  updateRoofTypeUI(sectionIdOnly) {
    const isBareMetal = this.roofType === 'bare-metal';
    const tooltip = document.querySelector(`#roof-type-tooltip-${sectionIdOnly}`);
    if (tooltip) {
      const rawText = tooltip.getAttribute('data-raw-metal-text') || '';
      const paintedText = tooltip.getAttribute('data-painted-text') || rawText;
      tooltip.textContent = isBareMetal ? rawText : paintedText;
    }
    const wrap = document.querySelector(`#roof-type-toggle-${sectionIdOnly}`)?.closest('.roof-type-toggle-wrap');
    if (wrap) {
      wrap.setAttribute('data-roof-type', isBareMetal ? 'bare-metal' : 'painted-roof');
    }
  }

  handleRoofTypeToggle() {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const toggle = document.querySelector(`#roof-type-toggle-${sectionIdOnly}`);
    if (toggle) {
      this.roofType = toggle.checked ? 'bare-metal' : 'painted-roof';
    }

    // Swap the info tooltip + state labels to match the selected roof type
    this.updateRoofTypeUI(sectionIdOnly);

    // Only update primer products (sealer/etch-primer), not everything
    this.updatePrimerProductsOnly();
  }

  handleFullCoverToggle() {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const toggle = document.querySelector(`#full-cover-toggle-${sectionIdOnly}`);
    if (toggle) {
      this.fullCover = toggle.checked;
    }

    // Recalculate all products when full cover changes
    this.updateCalculations();
  }

  handleConcreteRoofToggle() {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const toggle = document.querySelector(`#concrete-roof-toggle-${sectionIdOnly}`);
    if (toggle) {
      this.concreteRoof = toggle.checked;
    }

    // Recalculate all products when concrete roof changes
    this.updateCalculations();
  }

  resetToDefaults() {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');

    // Reset length to default
    this.length = this.defaultLength;
    const lengthInput = document.querySelector(`#length-input-${sectionIdOnly}`);
    if (lengthInput) {
      lengthInput.value = this.defaultLength.toFixed(1);
    }

    // Reset width to default
    this.width = this.defaultWidth;
    const widthInput = document.querySelector(`#width-input-${sectionIdOnly}`);
    if (widthInput) {
      widthInput.value = this.defaultWidth.toFixed(1);
    }

    // Reset area to default
    this.area = this.defaultArea;
    const areaInput = document.querySelector(`#area-input-${sectionIdOnly}`);
    if (areaInput) {
      areaInput.value = this.defaultArea.toFixed(1);
    }

    // Reset roof type to default (painted-roof)
    this.roofType = 'painted-roof';
    const roofToggle = document.querySelector(`#roof-type-toggle-${sectionIdOnly}`);
    if (roofToggle) {
      roofToggle.checked = false;
    }

    // Reset full cover to default (false)
    this.fullCover = false;
    const fullCoverToggle = document.querySelector(`#full-cover-toggle-${sectionIdOnly}`);
    if (fullCoverToggle) {
      fullCoverToggle.checked = false;
    }

    // Reset concrete roof to default (false)
    this.concreteRoof = false;
    const concreteRoofToggle = document.querySelector(`#concrete-roof-toggle-${sectionIdOnly}`);
    if (concreteRoofToggle) {
      concreteRoofToggle.checked = false;
    }

    // Reset colors to defaults (white for thermal coating)
    this.selectedColors = this.products.reduce((acc, p) => {
      if (p.handle === 'seam-tape') return acc;
      const normalizedHandle = this.normalizeHandle(p.handle);

      // Set default color to white for thermal coating (protective-top-coat)
      if (normalizedHandle === 'protective-top-coat' && p.variants) {
        // Find white variant
        const whiteVariant = p.variants.find((v) => {
          const color = (v.color || '').toString().trim().toLowerCase();
          return color === 'white';
        });
        if (whiteVariant?.color) {
          acc[normalizedHandle] = whiteVariant.color;
        } else if (p.colors?.length) {
          acc[normalizedHandle] = p.colors[0];
        }
      } else if (p.colors?.length) {
        acc[normalizedHandle] = p.colors[0];
      }
      return acc;
    }, {});

    // Clear any manually adjusted bucket counts by clearing breakdowns
    // This will force a fresh calculation
    this.breakdowns = [];
    this.manuallyAdjustedBuckets.clear();
    this.lastCalculatedArea = null;

    // Recalculate everything
    this.updateCalculations();
  }

  initializeProducts() {
    // Initialize items and selected colors
    // Exclude seam-tape and liquid-rubber-diy-2-piece-brush-kit (brush kit is in bonus, not calculator)
    this.items = this.products
      .filter((p) => p.handle !== 'seam-tape' && p.handle !== 'liquid-rubber-diy-2-piece-brush-kit')
      .map((p) => ({ data: p, selected: true }));

    this.selectedColors = this.products.reduce((acc, p) => {
      if (p.handle === 'seam-tape' || p.handle === 'liquid-rubber-diy-2-piece-brush-kit') return acc;
      const normalizedHandle = this.normalizeHandle(p.handle);

      // Set default color to white for thermal coating (protective-top-coat)
      if (normalizedHandle === 'protective-top-coat' && p.variants) {
        // Find white variant
        const whiteVariant = p.variants.find((v) => {
          const color = (v.color || '').toString().trim().toLowerCase();
          return color === 'white';
        });
        if (whiteVariant?.color) {
          acc[normalizedHandle] = whiteVariant.color;
        } else if (p.colors?.length) {
          acc[normalizedHandle] = p.colors[0];
        }
      } else if (p.colors?.length) {
        acc[normalizedHandle] = p.colors[0];
      }
      return acc;
    }, {});
  }

  getArea() {
    if (this.inputMode === 'area') {
      return Math.round(this.area * 100) / 100;
    } else if (this.inputMode === 'length_width') {
      return Math.round(this.length * this.width * 100) / 100;
    } else {
      // length mode: use length * default width
      return Math.round(this.length * this.defaultWidth * 100) / 100;
    }
  }

  /**
   * Convert a URL slug/handle into a human-readable title.
   * e.g. "waterproof-sealant" -> "Waterproof Sealant"
   */
  normalizeSlugToTitle(slug) {
    if (!slug || typeof slug !== 'string') return '';
    const cleaned = slug.replace(/[-_]+/g, ' ').trim();
    if (!cleaned) return '';
    return cleaned
      .split(' ')
      .filter((part) => part)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }

  /**
   * Build a human-readable cart note based on the current calculator inputs
   * and the page where the kit was configured.
   *
   * Examples:
   * - /products/waterproof-sealant  -> "Waterproof Sealant 25 m² (added from /products/waterproof-sealant)"
   * - /collections/caravans        -> "Caravan, 6 m (added from /collections/caravans)"
   */
  buildCartNoteFromCalculator() {
    const path = (globalThis.location && globalThis.location.pathname) || '';
    const area = this.getArea();

    // Always display the calculated area in the note
    const valueText = `${this.formatDecimal(area)} m²`;

    // Decide label based on URL and page title
    const pathParts = path.split('/').filter(Boolean);
    const handleFromPath = pathParts.length ? pathParts[pathParts.length - 1] : '';
    const normalizedTitleFromPath = this.normalizeSlugToTitle(handleFromPath);

    let label = (this.pageTitle && this.pageTitle.trim()) || '';

    if (path.startsWith('/collections/')) {
      // Collection pages: prefer actual collection title if available, otherwise
      // create a readable name from the handle, finally fall back to "Collection"
      if (!label) {
        label = normalizedTitleFromPath || 'Collection';
      }
    } else if (path.startsWith('/products/')) {
      // Product pages: prefer product title, otherwise titleized handle, then "Product"
      if (!label) {
        label = normalizedTitleFromPath || 'Product';
      }
    } else if (!label) {
      // Fallback for other pages
      label = 'Kit';
    }

    // For collection-style label we add a comma: "Caravan, 6 m"
    const needsComma = path.startsWith('/collections/');
    const baseLabel = needsComma ? `${label},` : label;

    return `${baseLabel} ${valueText}`;
  }

  /**
   * Update the cart note on Shopify with the current calculator details.
   * Uses the same /cart/update.js mechanism as the built-in cart note.
   */
  async updateCartNoteFromCalculator() {
    try {
      const note = this.buildCartNoteFromCalculator();
      if (!note) return;

      const updateUrl =
        (typeof routes !== 'undefined' && routes.cart_update_url) ||
        (globalThis.routes && globalThis.routes.cart_update_url) ||
        '/cart/update.js';

      let config;
      if (typeof fetchConfig !== 'undefined') {
        config = fetchConfig('javascript');
      } else {
        config = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
        };
      }

      // Ensure JSON body for cart/update endpoint
      config.headers = config.headers || {};
      config.headers['Content-Type'] = 'application/json';
      config.body = JSON.stringify({ note });

      await fetch(updateUrl, config);
    } catch (error) {
      // Fail silently, but log for debugging
      // eslint-disable-next-line no-console
      console.error('Failed to update cart note from caravan calculator', error);
    }
  }

  /**
   * Format a number to display with up to 3 decimal places, removing trailing zeros
   * @param {number} num - The number to format
   * @returns {string} - Formatted number string
   */
  formatDecimal(num) {
    if (num == null || Number.isNaN(num)) return '0';
    // Round to 3 decimal places to handle floating point precision issues
    const rounded = Math.round(num * 1000) / 1000;
    // Convert to string with up to 3 decimal places, then remove trailing zeros
    return rounded.toFixed(3).replace(/\.?0+$/, '');
  }

  isVisible(handle) {
    // If showEtchPrimer is enabled, always show etch-primer (as if toggle is on)
    if (handle === 'etch-primer' && this.showEtchPrimer) return true;
    // If raw metal toggle is not shown and showEtchPrimer is not enabled, never show etch-primer
    if (handle === 'etch-primer' && !this.showRawMetalToggle && !this.showEtchPrimer) return false;
    if (handle === 'sealer') return this.roofType !== 'bare-metal';
    if (handle === 'etch-primer') return this.roofType === 'bare-metal';
    if (handle === 'seam-tape') return false;
    if (handle === 'brush-roller') return false;
    if (handle === 'liquid-rubber-diy-2-piece-brush-kit') return false;
    return true;
  }

  calculateLitresNeeded(product, handle, area, fullCover) {
    // Check if concrete roof is enabled and product has rough surface coverage
    if (this.concreteRoof && product.coverageRough && product.coverageRough > 0) {
      // Use rough surface coverage
      const areaPercentage = product.areaPercentage || 1;
      const isWaterproofSealant = handle === 'waterproof-sealant';
      const effectiveAreaPercentage = isWaterproofSealant && fullCover ? 1 : areaPercentage;
      const effectiveArea = Number(area) * Number(effectiveAreaPercentage);
      const litres = effectiveArea * Number(product.coverageRough);
      return Math.round(litres * 1000) / 1000;
    }

    // Use normal coverage rate function
    // Try normalized handle first, then original handle
    if (this.coverageRates[handle]) {
      return this.coverageRates[handle](area, fullCover);
    }
    if (this.coverageRates[product.handle]) {
      return this.coverageRates[product.handle](area, fullCover);
    }
    return 0;
  }

  calculateBuckets(product, area, fullCover = true) {
    if (!product || area <= 0) return [];

    const handle = this.normalizeHandle(product.handle);

    // rapid-cure-spray is calculated separately based on sealant litres, not area
    // It should never go through this method - early return as safeguard
    if (handle === 'rapid-cure-spray') {
      console.warn(
        'rapid-cure-spray should not be calculated via calculateBuckets() - use calculateRapidCureSpray() instead'
      );
      return [];
    }

    if (handle === 'geo-textile') {
      return this.calculateGeoTextile(product, area);
    }

    const variants = this.extractBucketVariants(product);
    if (!variants.length) {
      console.warn('No variants found for', product.handle);
      return [];
    }

    const litresNeeded = this.calculateLitresNeeded(product, handle, area, fullCover);
    if (litresNeeded <= 0) return [];

    console.log(`Calculating buckets for ${product.handle}: area=${area}m², litresNeeded=${litresNeeded}L`);
    const result = this.findOptimalCombo(litresNeeded, variants);
    console.log(`Result for ${product.handle}:`, result.map((b) => `${b.count}x${b.size}L`).join(', '));
    return result;
  }

  extractBucketVariants(product) {
    const bySize = new Map();
    product.variants.forEach((v) => {
      // Match both "L" (liters) and "kg" (kilograms) - treat kg the same as L for calculation
      const m = /(\d+(?:\.\d+)?)\s*(?:L|kg)/i.exec(v.label || '');
      if (!m) {
        console.warn('Variant label does not match size pattern:', v.label);
        return;
      }
      const size = +m[1];
      const price = +v.price;
      if (!bySize.has(size) || price < bySize.get(size).price) {
        bySize.set(size, {
          size,
          price,
          variantId: v.variantId,
          imageUrl: v.imageUrl || '/placeholder.png',
          label: v.label || '',
          color: v.color || '',
        });
      }
    });

    const variants = Array.from(bySize.values()).sort((a, b) => b.size - a.size);
    console.log(
      'Extracted variants for',
      product.handle,
      ':',
      variants.map((v) => `${v.size}L @ $${v.price}`).join(', ')
    );
    return variants;
  }

  findOptimalCombo(litresNeeded, variants) {
    // Use factor 1000 to support 3 decimal places (e.g., 0.125)
    const factor = 1000;
    const needVol = Math.ceil(litresNeeded * factor);
    const biggest = Math.round(variants[0].size * factor);
    const cap = needVol + biggest;

    const dp = new Array(cap + 1).fill(null);
    dp[0] = { cost: 0, counts: new Array(variants.length).fill(0) };

    for (let vol = 0; vol <= cap; vol++) {
      const entry = dp[vol];
      if (!entry) continue;

      variants.forEach((v, i) => {
        const nextVol = vol + Math.round(v.size * factor);
        if (nextVol > cap) return;

        const currentCount = entry.counts[i];
        const isLargest = i === 0;
        if (!isLargest && currentCount >= 3) return;

        const nextCost = entry.cost + v.price;
        const nextCounts = entry.counts.slice();
        nextCounts[i]++;

        const prev = dp[nextVol];
        if (!prev || nextCost < prev.cost) {
          dp[nextVol] = { cost: nextCost, counts: nextCounts };
        }
      });
    }

    let best = null;
    for (let vol = needVol; vol <= cap; vol++) {
      const entry = dp[vol];
      if (!entry) continue;
      if (!best || entry.cost < best.entry.cost) {
        best = { vol, entry };
      }
    }

    if (!best) return [];

    return variants.flatMap((v, i) => {
      const c = best.entry.counts[i];
      return c === 0
        ? []
        : [
            {
              size: v.size,
              count: c,
              price: v.price,
              variantId: v.variantId,
              imageUrl: v.imageUrl,
              label: v.label,
              color: v.color,
            },
          ];
    });
  }

  getLengthFromLabel(label) {
    if (!label) return undefined;
    const m = /x\s*(\d+)\s*m\b/i.exec(label);
    return m ? Number.parseInt(m[1], 10) : undefined;
  }

  calculateGeoTextileRemainder(remainder) {
    if (remainder <= 20) {
      return { count100: 0, count50: 0, count20: 1 };
    }
    if (remainder <= 50) {
      return { count100: 0, count50: 1, count20: 0 };
    }
    if (remainder <= 70) {
      return { count100: 0, count50: 1, count20: 1 };
    }
    return { count100: 1, count50: 0, count20: 0 };
  }

  optimizeGeoTextileCounts(counts) {
    let { count100, count50, count20 } = counts;

    if (count20 >= 2) {
      count50 += Math.floor(count20 / 2);
      count20 = count20 % 2;
    }
    if (count50 >= 2) {
      count100 += Math.floor(count50 / 2);
      count50 = count50 % 2;
    }

    return { count100, count50, count20 };
  }

  buildGeoTextileResult(counts, variants) {
    const result = [];
    const { count100, count50, count20 } = counts;

    if (count100 > 0 && variants.v100) {
      result.push({ ...variants.v100, count: count100, size: variants.v100.size });
    }
    if (count50 > 0 && variants.v50) {
      result.push({ ...variants.v50, count: count50, size: variants.v50.size });
    }
    if (count20 > 0 && variants.v20) {
      result.push({ ...variants.v20, count: count20, size: variants.v20.size });
    }

    return result;
  }

  calculateGeoTextile(product, area) {
    // Use area multiplier if available, otherwise default to 1
    // Rule: 1m of geo textile per 1L of waterproof sealant (multiplied by area multiplier)
    const multiplier = product.multiplier || 1;

    // Always calculate based on waterproof sealant quantity (area-based)
    // Find waterproof sealant item to calculate its litres
    const sealantItem = this.items.find((i) => this.normalizeHandle(i.data.handle) === 'waterproof-sealant');

    if (!sealantItem) {
      // Fallback: if sealant not found, return empty result
      return this.buildGeoTextileResult(
        { count100: 0, count50: 0, count20: 0 },
        {
          v100: null,
          v50: null,
          v20: null,
        }
      );
    }

    // Calculate sealant litres needed for the area
    const useFull = this.fullCover;
    let sealantLitres = 0;

    // Check if concrete roof is enabled and product has rough surface coverage
    if (this.concreteRoof && sealantItem.data.coverageRough && sealantItem.data.coverageRough > 0) {
      const areaPercentage = sealantItem.data.areaPercentage || 1;
      const effectiveAreaPercentage = useFull ? 1 : areaPercentage;
      const effectiveArea = Number(area) * Number(effectiveAreaPercentage);
      sealantLitres = effectiveArea * Number(sealantItem.data.coverageRough);
      sealantLitres = Math.round(sealantLitres * 1000) / 1000;
    } else if (this.coverageRates['waterproof-sealant']) {
      // Use normal coverage rate function
      sealantLitres = this.coverageRates['waterproof-sealant'](area, useFull);
    }

    // Calculate geo textile: 1m per L of sealant, multiplied by area multiplier
    const requiredMeters = Math.ceil(sealantLitres * multiplier);

    const variants = product.variants.filter((v) => v.size !== undefined);

    const variantMap = {
      v100: variants.find((v) => this.getLengthFromLabel(v.label) === 100),
      v50: variants.find((v) => this.getLengthFromLabel(v.label) === 50),
      v20: variants.find((v) => this.getLengthFromLabel(v.label) === 20),
    };

    let counts = { count100: 0, count50: 0, count20: 0 };

    if (requiredMeters > 0) {
      counts.count100 = Math.floor(requiredMeters / 100);
      const remainder = requiredMeters % 100;

      if (remainder > 0) {
        const remainderCounts = this.calculateGeoTextileRemainder(remainder);
        counts.count100 += remainderCounts.count100;
        counts.count50 += remainderCounts.count50;
        counts.count20 += remainderCounts.count20;
      }

      counts = this.optimizeGeoTextileCounts(counts);
    }

    return this.buildGeoTextileResult(counts, variantMap);
  }

  calculateVariants(product, area) {
    const handle = this.normalizeHandle(product.handle);

    // Check if product has a multiplier (length-based product)
    if (product.multiplier && product.multiplier > 0) {
      return this.calculateGeoTextile(product, area);
    }

    if (handle === 'geo-textile') {
      return this.calculateGeoTextile(product, area);
    }

    if (handle === 'brush-roller') {
      const variants = product.variants || [];
      const isBrushOnly = area < 5;
      const chosen =
        variants.find((v) => {
          const label = (v.label || '').toLowerCase();
          const hasBrush = label.includes('brush');
          const hasRoller = label.includes('roller') || label.includes('+');
          return isBrushOnly ? hasBrush && !hasRoller : hasRoller;
        }) || variants[0];

      if (!chosen) return [];

      return [
        {
          ...chosen,
          count: 1,
          imageUrl: isBrushOnly
            ? '/images/products/liquid-rubber-diy-brush-set-caravan-roof.webp'
            : '/images/products/liquid-rubber-diy-brush-roller-set-caravan-roof.webp',
        },
      ];
    }

    if (handle === 'rapid-cure-spray') {
      // This is calculated based on sealant litres, handled separately
      return [];
    }

    return [];
  }

  updateCalculations() {
    const area = this.getArea();
    this.updateLengthDisplay();
    this.updateAreaDisplay();

    // Track previous area to determine if we should preserve manual counts
    this.lastCalculatedArea = area;

    // Preserve manually adjusted bucket counts before recalculating
    // Only preserve counts for buckets that were manually adjusted by the user
    // Key format: handle:variantId -> count (using variantId as primary key for better matching)
    const manualCounts = new Map();
    if (this.breakdowns && this.breakdowns.length > 0) {
      this.breakdowns.forEach((bd) => {
        bd.buckets.forEach((bucket) => {
          // Only preserve counts for buckets that were manually adjusted
          if (bucket.variantId) {
            const key = `${bd.handle}:${bucket.variantId}`;
            if (this.manuallyAdjustedBuckets.has(key)) {
              // Store the manually adjusted count
              manualCounts.set(key, bucket.count);
            }
          }
        });
      });
    }

    // Step 1: Sort items to ensure waterproof-sealant is calculated FIRST
    // This is critical because rapid-cure-spray depends on sealant litres
    const sortedItems = [...this.items].sort((a, b) => {
      const aHandle = this.normalizeHandle(a.data.handle);
      const bHandle = this.normalizeHandle(b.data.handle);

      // waterproof-sealant should come first
      if (aHandle === 'waterproof-sealant') return -1;
      if (bHandle === 'waterproof-sealant') return 1;

      // rapid-cure-spray should come last (will be filtered out anyway)
      if (aHandle === 'rapid-cure-spray') return 1;
      if (bHandle === 'rapid-cure-spray') return -1;

      return 0;
    });

    // Step 2: Calculate breakdowns for all items except rapid-cure-spray
    // This ensures waterproof-sealant is fully calculated first
    const prelim = sortedItems
      .map((item) => {
        const breakdown = this.calculateItemBreakdown(item, area);
        // Log sealant calculation for debugging
        if (this.normalizeHandle(item.data.handle) === 'waterproof-sealant') {
          const totalLitres = breakdown.buckets.reduce((sum, bk) => {
            const size = Number(bk.size) || 0;
            const count = Number(bk.count) || 0;
            return sum + size * count;
          }, 0);
          console.log('Sealant breakdown calculated:', {
            handle: breakdown.handle,
            buckets: breakdown.buckets,
            bucketCount: breakdown.buckets.length,
            totalLitres,
            bucketDetails: breakdown.buckets.map((b) => ({
              size: b.size,
              sizeType: typeof b.size,
              count: b.count,
              countType: typeof b.count,
              litres: (Number(b.size) || 0) * (Number(b.count) || 0),
            })),
          });
        }
        return breakdown;
      })
      .filter((b) => b.handle !== 'rapid-cure-spray');

    // Step 3: Verify sealant exists before calculating rapid-cure-spray
    const sealantInPrelim = prelim.find((b) => this.normalizeHandle(b.handle) === 'waterproof-sealant');
    if (!sealantInPrelim) {
      console.warn(
        'Waterproof-sealant not found in prelim breakdowns. Available handles:',
        prelim.map((b) => b.handle)
      );
    }

    // Step 4: Calculate rapid-cure-spray AFTER sealant is calculated
    const list = this.addRapidCureSprayIfNeeded(prelim);

    // Step 5: Restore manually adjusted bucket counts
    // Match by variantId to ensure we're restoring the correct bucket
    // Only restore if the bucket still exists in the new calculation
    if (manualCounts.size > 0) {
      // Track which buckets still exist in the new calculation
      const existingBuckets = new Set();

      list.forEach((bd) => {
        bd.buckets.forEach((bucket) => {
          if (bucket.variantId) {
            const key = `${bd.handle}:${bucket.variantId}`;
            existingBuckets.add(key);
            if (manualCounts.has(key)) {
              // Restore the manually adjusted count
              // This preserves user's manual adjustments even when area changes
              const preservedCount = manualCounts.get(key);
              bucket.count = preservedCount;
            }
          }
        });
      });

      // Clean up manually adjusted buckets that no longer exist in the calculation
      this.manuallyAdjustedBuckets.forEach((key) => {
        if (!existingBuckets.has(key)) {
          this.manuallyAdjustedBuckets.delete(key);
        }
      });
    }

    // Organize breakdowns (brush-roller last)
    this.breakdowns = this.organizeBreakdowns(list);

    // Update UI - try to update only variants first (preserves color swatches)
    // If products don't exist yet, fall back to full render
    if (!this.updateVariantsOnly()) {
      this.renderProductBreakdown();
    }
    this.updatePricing();
    // Update bonus kit (async - fire and forget, will update UI when variant info is fetched)
    this.updateBonusKit(area).catch((err) => console.warn('Error updating bonus kit:', err));
  }

  /**
   * Helper method to update a single primer product (sealer or etch-primer)
   * @param {Object} item - The product item to update
   * @param {string} handle - The normalized handle ('sealer' or 'etch-primer')
   * @param {number} area - The calculated area
   * @param {HTMLElement} container - The product breakdown container element
   */
  updateSinglePrimerProduct(item, handle, area, container) {
    // Ensure breakdown exists - if not, calculate it without full render
    let breakdown = this.breakdowns.find((bd) => this.normalizeHandle(bd.handle) === handle);
    if (!breakdown) {
      breakdown = this.calculateItemBreakdown(item, area);
      this.breakdowns.push(breakdown);
    }

    // Recalculate buckets
    const buckets = this.calculateBuckets(item.data, area, true);
    const matchedBuckets = item.data.hasColorVariants ? this.matchVariantsWithColors(buckets, item, handle) : buckets;
    breakdown.buckets = matchedBuckets;

    // Update UI
    const productCard = container.querySelector(`.product-card[data-handle="${handle}"]`);
    if (!productCard) {
      return; // Product card doesn't exist - will be created on next full render
    }

    const isVisible = this.isVisible(handle);
    productCard.style.display = isVisible ? '' : 'none';

    if (!isVisible) {
      return; // Product is hidden, no need to update variants
    }

    // Update variants column only (preserves color swatches in title column)
    const variantsColumn = productCard.querySelector('.product-card-variants-column');
    if (!variantsColumn) {
      return;
    }

    const variantsDiv = variantsColumn.querySelector('.product-card-variants');
    if (!variantsDiv) {
      return;
    }

    // Use a temporary marker to prevent mutation observers from interfering
    variantsDiv.dataset.updating = 'true';
    variantsDiv.innerHTML = '';
    this.renderProductVariants(breakdown, variantsDiv);
    delete variantsDiv.dataset.updating;
  }

  /**
   * Update only the primer products (sealer/etch-primer) when roof type changes
   * This shows/hides the appropriate product and updates its variants without affecting other products
   */
  updatePrimerProductsOnly() {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const container = document.querySelector(`#product-breakdown-${sectionIdOnly}`);
    if (!container) {
      this.updateCalculations();
      return;
    }

    const area = this.getArea();

    // Update sealer product
    const sealerItem = this.items.find((i) => this.normalizeHandle(i.data.handle) === 'sealer');
    if (sealerItem) {
      this.updateSinglePrimerProduct(sealerItem, 'sealer', area, container);
    }

    // Update etch-primer product
    const etchPrimerItem = this.items.find((i) => this.normalizeHandle(i.data.handle) === 'etch-primer');
    if (etchPrimerItem) {
      this.updateSinglePrimerProduct(etchPrimerItem, 'etch-primer', area, container);
    }

    // Update pricing
    this.updatePricing();
  }

  /**
   * Update only the variants column for all products without touching color swatches
   * Returns true if update was successful, false if products don't exist yet (needs full render)
   */
  updateVariantsOnly() {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const container = document.querySelector(`#product-breakdown-${sectionIdOnly}`);
    if (!container) return false;

    let allUpdated = true;

    this.breakdowns.forEach((bd) => {
      if (!this.isVisible(bd.handle)) return;

      const productCard = container.querySelector(`.product-card[data-handle="${bd.handle}"]`);
      if (!productCard) {
        allUpdated = false;
        return;
      }

      // Find the variants column
      const variantsColumn = productCard.querySelector('.product-card-variants-column');
      if (!variantsColumn) {
        allUpdated = false;
        return;
      }

      // Clear existing variants
      const variantsDiv = variantsColumn.querySelector('.product-card-variants');
      if (!variantsDiv) {
        allUpdated = false;
        return;
      }

      // Re-render only the variants for this product (preserves color swatches)
      variantsDiv.innerHTML = '';
      this.renderProductVariants(bd, variantsDiv);
    });

    return allUpdated;
  }

  /**
   * Optimized method to update variants for a single product when only color changes
   * This avoids full recalculation and re-rendering of all products
   */
  updateProductVariants(productHandle) {
    const normalizedHandle = this.normalizeHandle(productHandle);

    // Find the item and breakdown for this product
    const item = this.items.find((i) => this.normalizeHandle(i.data.handle) === normalizedHandle);
    const breakdown = this.breakdowns.find((bd) => this.normalizeHandle(bd.handle) === normalizedHandle);

    if (!item || !breakdown) {
      console.warn('Product not found for variant update:', productHandle);
      // Fallback to full recalculation if product not found
      this.updateCalculations();
      return;
    }

    // Get the area (needed for variant matching)
    const area = this.getArea();

    // Recalculate buckets for this product only (preserve counts)
    const manualCounts = new Map();
    breakdown.buckets.forEach((bucket) => {
      const key = `${breakdown.handle}:${bucket.size}:${bucket.variantId || ''}`;
      manualCounts.set(key, bucket.count);
    });

    // Recalculate buckets
    let buckets = [];
    const hasMultiplier = item.data.multiplier && item.data.multiplier > 0;

    if (hasMultiplier || ['geo-textile', 'seam-tape', 'brush-roller'].includes(normalizedHandle)) {
      buckets = this.calculateVariants(item.data, area);
    } else {
      buckets = this.calculateBuckets(item.data, area, true);
    }

    // Re-match variants with the new color
    buckets = this.matchVariantsWithColors(buckets, item, normalizedHandle);

    // Restore manually adjusted counts
    buckets.forEach((bucket) => {
      const key = `${breakdown.handle}:${bucket.size}:${bucket.variantId || ''}`;
      if (manualCounts.has(key)) {
        bucket.count = manualCounts.get(key);
      }
    });

    // Update the breakdown
    breakdown.buckets = buckets;

    // Update only this product's DOM element (not the entire breakdown)
    this.updateSingleProductBreakdown(breakdown);

    // Update pricing (needs full calculation, but that's fine)
    this.updatePricing();
  }

  /**
   * Update product image when color changes for thermal coating
   */
  updateProductImageForColor(productHandle, colorValue) {
    const normalizedHandle = this.normalizeHandle(productHandle);

    // Update for protective top coat (thermal coating) and tough coat
    if (normalizedHandle !== 'protective-top-coat' && normalizedHandle !== 'tough-coat') {
      return;
    }

    // Find the product item
    const item = this.items.find((i) => this.normalizeHandle(i.data.handle) === normalizedHandle);
    if (!item?.data?.variants) {
      return;
    }

    // Find variant with matching color (case-insensitive)
    const colorValueLower = (colorValue || '').toString().trim().toLowerCase();
    const matchingVariant = item.data.variants.find((v) => {
      const variantColor = (v.color || '').toString().trim().toLowerCase();
      return variantColor === colorValueLower;
    });

    // If no exact match, try to find first variant with the color in the value
    let variant = matchingVariant;
    if (!variant) {
      variant = item.data.variants.find((v) => {
        const variantColor = (v.color || '').toString().trim().toLowerCase();
        return variantColor.includes(colorValueLower) || colorValueLower.includes(variantColor);
      });
    }

    // Get image URL from variant or use first variant's image or product featured image
    let imageUrl = '/placeholder.png';
    if (variant?.imageUrl) {
      imageUrl = variant.imageUrl;
    } else if (variant?.image) {
      imageUrl = variant.image;
    } else if (item.data.variants.length > 0 && item.data.variants[0]?.imageUrl) {
      imageUrl = item.data.variants[0].imageUrl;
    } else if (item.data.featured_image) {
      imageUrl = item.data.featured_image;
    }

    // Find and update the product image element
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const productCard = document.querySelector(
      `#product-breakdown-${sectionIdOnly} .product-card[data-handle="${item.data.handle}"]`
    );

    if (productCard) {
      // Update main product image
      const img = productCard.querySelector('.product-card-image');
      if (img && img.src !== imageUrl) {
        img.src = imageUrl;
      }

      // Update variant images in the variant container
      // Use setTimeout to ensure DOM is updated after updateProductVariants completes
      setTimeout(() => {
        const variantImages = productCard.querySelectorAll('.variant-image');
        variantImages.forEach((variantImg) => {
          const variantItem = variantImg.closest('.variant-item');
          if (variantItem) {
            const variantLabel = variantItem.querySelector('.variant-label');
            if (variantLabel) {
              const labelText = variantLabel.textContent.trim();
              // Try to extract size from label (e.g., "4L (8m²) / Evening Haze", "4L", or "1kg")
              // Match both "L" (liters) and "kg" (kilograms) - treat kg the same as L
              const sizeMatch = /(\d+)(?:L|kg)/i.exec(labelText);
              if (sizeMatch) {
                const size = Number.parseInt(sizeMatch[1], 10);
                // Find variant with matching size and color
                const matchingVariant = item.data.variants.find((v) => {
                  const variantSize = typeof v.size === 'number' ? v.size : Number.parseInt(v.size, 10);
                  const variantColor = (v.color || '').toString().trim().toLowerCase();
                  return variantSize === size && variantColor === colorValueLower;
                });

                if (matchingVariant) {
                  const variantImageUrl = matchingVariant.imageUrl || matchingVariant.image || imageUrl;
                  // Only update if the URL is different to avoid unnecessary reloads
                  if (variantImageUrl && variantImageUrl !== '/placeholder.png' && variantImg.src !== variantImageUrl) {
                    variantImg.src = variantImageUrl;
                    console.log('Updated variant image:', {
                      size,
                      color: colorValue,
                      imageUrl: variantImageUrl,
                    });
                  }
                } else {
                  // If no exact match, try to find by size only and use that variant's image
                  const sizeOnlyVariant = item.data.variants.find((v) => {
                    const variantSize = typeof v.size === 'number' ? v.size : Number.parseInt(v.size, 10);
                    return variantSize === size;
                  });
                  if (sizeOnlyVariant?.imageUrl && variantImg.src !== sizeOnlyVariant.imageUrl) {
                    variantImg.src = sizeOnlyVariant.imageUrl;
                  }
                }
              }
            }
          }
        });
      }, 0);
    }
  }

  /**
   * Update a single product's breakdown in the DOM without re-rendering everything
   */
  updateSingleProductBreakdown(breakdown) {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const productCard = document.querySelector(
      `#product-breakdown-${sectionIdOnly} .product-card[data-handle="${breakdown.handle}"]`
    );

    if (!productCard) {
      // If product card doesn't exist, fallback to full render
      this.renderProductBreakdown();
      return;
    }

    // Find the variants column
    const variantsColumn = productCard.querySelector('.product-card-variants-column');
    if (!variantsColumn) {
      // If variants column doesn't exist, fallback to full render
      this.renderProductBreakdown();
      return;
    }

    // Clear existing variants
    const variantsDiv = variantsColumn.querySelector('.product-card-variants');
    if (variantsDiv) {
      variantsDiv.innerHTML = '';
    } else {
      // If variants div doesn't exist, fallback to full render
      this.renderProductBreakdown();
      return;
    }

    // Re-render variants for this product only
    this.renderProductVariants(breakdown, variantsDiv);
  }

  /**
   * Create the updateAllSwatchSelection function for a specific swatches container
   * @param {HTMLElement} swatchesContainer - The swatches container element
   * @param {HTMLElement|null} dropdown - The dropdown element (if exists)
   * @returns {Function} The update function
   */
  createUpdateAllSwatchSelectionFunction(swatchesContainer, dropdown) {
    return () => {
      // Find the checked input from ALL swatches (visible and hidden) - only in this container
      const allSwatchInputs = swatchesContainer.querySelectorAll('input[type="radio"]');
      let selectedInput = null;
      let selectedColorValue = null;

      allSwatchInputs.forEach((input) => {
        if (input.checked) {
          selectedInput = input;
          selectedColorValue = input.value;
        }
      });

      // Update all visible swatches - only within this specific container
      swatchesContainer.querySelectorAll('.custom-color-swatch').forEach((swatch) => {
        const swatchInput = swatch.querySelector('input[type="radio"]');
        const swatchLabel = swatch.querySelector('.custom-color-swatch__label');
        const isSelected = swatchInput && swatchInput === selectedInput;
        this.updateSwatchSelectionState(swatch, swatchLabel, isSelected);
      });

      // Update all dropdown rows (only if dropdown exists)
      if (dropdown) {
        dropdown.querySelectorAll('.color-swatches-dropdown-row').forEach((row) => {
          const rowInput = row.querySelector('input[type="radio"]');
          const swatch = row.querySelector('.custom-color-swatch');
          const label = swatch ? swatch.querySelector('.custom-color-swatch__label') : null;

          // Always remove selected classes first
          row.classList.remove('color-swatches-dropdown-row--selected');
          if (swatch) {
            swatch.classList.remove('custom-color-swatch--selected');
          }
          if (label) {
            label.classList.remove('custom-color-swatch--selected');
          }

          // Add selected classes if this row matches the selected color value
          if (selectedColorValue && rowInput && rowInput.value === selectedColorValue) {
            row.classList.add('color-swatches-dropdown-row--selected');
            if (swatch) {
              swatch.classList.add('custom-color-swatch--selected');
            }
            if (label) {
              label.classList.add('custom-color-swatch--selected');
            }
          }
        });
      }
    };
  }

  /**
   * Handle dropdown row click for color selection
   * @param {Object} options - Configuration options
   * @param {HTMLElement} options.dropdownRow - The dropdown row element
   * @param {HTMLElement} options.clonedSwatch - The cloned swatch element
   * @param {HTMLElement} options.swatch - The original swatch element
   * @param {HTMLElement} options.swatchesContainer - The swatches container
   * @param {HTMLElement} options.dropdown - The dropdown element
   * @param {HTMLElement} options.showMoreButton - The show more button
   * @param {string} options.productHandle - The product handle
   * @param {boolean} options.isProtectiveTopCoat - Whether this is protective top coat
   * @param {Function} options.updateAllSwatchSelection - Function to update swatch selection
   */
  handleDropdownRowClick(options) {
    const {
      dropdownRow,
      clonedSwatch,
      swatch,
      swatchesContainer,
      dropdown,
      showMoreButton,
      productHandle,
      isProtectiveTopCoat,
      updateAllSwatchSelection,
    } = options;
    dropdownRow.addEventListener('click', (e) => {
      e.stopPropagation();
      const input = clonedSwatch.querySelector('input[type="radio"]');
      if (input) {
        // Get color value from the input's value attribute (most reliable)
        const originalInput = swatch.querySelector('input[type="radio"]');
        const colorValue =
          originalInput?.value || input.value || swatch.dataset.value || swatch.getAttribute('title') || '';
        const normalizedHandle = this.normalizeHandle(productHandle);

        // Update selected color
        if (colorValue) {
          this.selectedColors[normalizedHandle] = colorValue;
          console.log('Color selected from dropdown:', {
            handle: productHandle,
            normalizedHandle,
            colorValue,
            inputValue: originalInput?.value,
            selectedColors: this.selectedColors,
          });
        }

        // Find and check the corresponding visible swatch input (by color value, not variant ID)
        const visibleInput = this.findVisibleInputByColor(swatchesContainer, colorValue, input.dataset.variantId);

        if (visibleInput) {
          this.checkInputAndUncheckOthers(swatchesContainer, visibleInput);
        } else {
          // If not in visible set, check the original hidden one
          this.checkInputAndUncheckOthers(swatchesContainer, originalInput);
        }

        // Update visual selection state
        updateAllSwatchSelection();

        // Optimize: Only update variants for this specific product
        this.updateProductVariants(productHandle);

        // Update product and variant images for thermal coating when color changes (after variants are updated)
        if (isProtectiveTopCoat) {
          this.updateProductImageForColor(productHandle, colorValue);
        }

        // Close dropdown after selection
        dropdown.style.display = 'none';
        showMoreButton.setAttribute('aria-expanded', 'false');
        showMoreButton.textContent = 'Show more';
      }
    });
  }

  /**
   * Handle color selection from a swatch click
   * @param {Event} e - The click event
   * @param {Object} options - Configuration options
   * @param {HTMLElement} options.input - The radio input element
   * @param {HTMLElement} options.swatch - The swatch element
   * @param {HTMLElement} options.swatchesContainer - The swatches container
   * @param {string} options.productHandle - The product handle
   * @param {boolean} options.isProtectiveTopCoat - Whether this is protective top coat
   * @param {boolean} options.isToughCoat - Whether this is tough coat
   * @param {Function} options.updateColorLabels - Function to update color labels
   */
  handleSwatchColorSelection(e, options) {
    const { input, swatch, swatchesContainer, productHandle, isProtectiveTopCoat, isToughCoat, updateColorLabels } =
      options;
    // Don't interfere if clicking directly on the input (let native behavior handle it)
    if (e.target.type === 'radio' || e.target.tagName === 'INPUT') {
      return;
    }

    // Only stop propagation on click, not on hover - this preserves tooltip functionality
    if (e.type === 'click') {
      e.stopPropagation();
    }

    // Uncheck all other inputs in the container first
    swatchesContainer.querySelectorAll('input[type="radio"]').forEach((inp) => {
      if (inp !== input) {
        inp.checked = false;
      }
    });

    // Check the input
    input.checked = true;

    // Get color value before triggering change
    const colorValue = input.value || swatch.dataset.value || swatch.getAttribute('title') || '';
    const normalizedHandle = this.normalizeHandle(productHandle);

    // Manually trigger change event immediately
    // Create a synthetic change event that will bubble properly
    const changeEvent = new Event('change', {
      bubbles: true,
      cancelable: true,
      composed: true,
    });

    // Dispatch the event directly on the input
    // Use requestAnimationFrame to ensure DOM is updated first
    requestAnimationFrame(() => {
      input.dispatchEvent(changeEvent);

      // Also directly call the update logic as a backup
      if (colorValue) {
        this.selectedColors[normalizedHandle] = colorValue;
        console.log('Color selected for Tough Coat:', {
          originalHandle: productHandle,
          normalizedHandle,
          colorValue,
          selectedColors: this.selectedColors,
        });
        updateColorLabels(colorValue);
        this.updateProductVariants(productHandle);
        // Also update image for tough-coat if it has color variants
        if (isProtectiveTopCoat || isToughCoat) {
          this.updateProductImageForColor(productHandle, colorValue);
        }
      }
    });

    // Also dispatch after a short delay as backup to ensure it's caught
    setTimeout(() => {
      // Double-check the input is still checked (in case something unchecks it)
      if (input.checked) {
        const backupEvent = new Event('change', {
          bubbles: true,
          cancelable: true,
          composed: true,
        });
        input.dispatchEvent(backupEvent);
      }
    }, 50);
  }

  /**
   * Set up change listener for color swatches
   * @param {HTMLElement} swatchesContainer - The swatches container
   * @param {string} productHandle - The product handle
   * @param {boolean} isProtectiveTopCoat - Whether this is protective top coat
   * @param {boolean} isToughCoat - Whether this is tough coat
   * @param {Function} updateAllSwatchSelection - Function to update swatch selection
   * @param {Function} updateColorLabels - Function to update color labels
   */
  setupSwatchChangeListener(
    swatchesContainer,
    productHandle,
    isProtectiveTopCoat,
    isToughCoat,
    updateAllSwatchSelection,
    updateColorLabels
  ) {
    swatchesContainer.addEventListener(
      'change',
      (e) => {
        if (e.target.type === 'radio') {
          // Stop propagation to prevent global handlers from interfering
          e.stopPropagation();

          // Update visual selection state first (only for this product's swatches)
          updateAllSwatchSelection();

          // Update variant selection when color changes
          const selectedInput = e.target;
          const selectedSwatch = selectedInput.closest('.custom-color-swatch');
          if (selectedSwatch) {
            // Get color value from the input's value attribute (most reliable)
            // The input value should match the variant color exactly
            const colorValue =
              selectedInput.value || selectedSwatch.dataset.value || selectedSwatch.getAttribute('title') || '';
            const normalizedHandle = this.normalizeHandle(productHandle);

            if (colorValue) {
              // Update selected color for this product only
              this.selectedColors[normalizedHandle] = colorValue;
              console.log('Color changed via change event:', {
                originalHandle: productHandle,
                normalizedHandle,
                colorValue,
                selectedColors: this.selectedColors,
              });

              // Update color labels
              updateColorLabels(colorValue);

              // Optimize: Only update variants for this specific product, not full recalculation
              this.updateProductVariants(productHandle);

              // Update product and variant images for thermal coating and tough coat when color changes
              if (isProtectiveTopCoat || isToughCoat) {
                this.updateProductImageForColor(productHandle, colorValue);
              }
            }
          }
        }
      },
      true
    ); // Use capture phase to handle before other listeners
  }

  /**
   * Render variants for a single product (extracted from renderProductBreakdown)
   */
  renderProductVariants(breakdown, variantsDiv) {
    if (!breakdown.buckets || breakdown.buckets.length === 0) {
      return;
    }

    breakdown.buckets.forEach((bucket) => {
      const variantItem = document.createElement('div');
      variantItem.className = 'variant-item';

      const img = document.createElement('img');
      img.src = bucket.imageUrl || '/placeholder.png';
      img.alt = bucket.label || '';
      img.className = 'variant-image';

      const variantInfo = document.createElement('div');
      variantInfo.className = 'variant-info';

      const label = document.createElement('div');
      label.className = 'variant-label';
      label.textContent = bucket.label || `${this.formatDecimal(bucket.size)}L`;

      const price = document.createElement('div');
      price.className = 'variant-price';
      price.textContent = `AUD ${(bucket.price || 0).toFixed(2)}`;

      variantInfo.appendChild(label);
      variantInfo.appendChild(price);

      const countDiv = document.createElement('div');
      countDiv.className = 'variant-count';

      const countInput = document.createElement('input');
      countInput.type = 'number';
      countInput.className = 'variant-count-input';
      countInput.value = bucket.count;
      countInput.min = 0;

      const decreaseBtn = document.createElement('button');
      decreaseBtn.className = 'variant-count-btn';
      decreaseBtn.textContent = '-';
      decreaseBtn.addEventListener('click', () => {
        if (bucket.count > 0) {
          bucket.count--;
          countInput.value = bucket.count;
          // Mark this bucket as manually adjusted
          const key = `${breakdown.handle}:${bucket.variantId || ''}`;
          this.manuallyAdjustedBuckets.add(key);
          this.updateCalculations(); // Full recalculation needed for quantity changes
        }
      });

      countInput.addEventListener('change', (e) => {
        bucket.count = Math.max(0, Number.parseInt(e.target.value, 10) || 0);
        countInput.value = bucket.count;
        // Mark this bucket as manually adjusted
        const key = `${breakdown.handle}:${bucket.variantId || ''}`;
        this.manuallyAdjustedBuckets.add(key);
        this.updateCalculations(); // Full recalculation needed for quantity changes
      });

      const increaseBtn = document.createElement('button');
      increaseBtn.className = 'variant-count-btn';
      increaseBtn.textContent = '+';
      increaseBtn.addEventListener('click', () => {
        bucket.count++;
        countInput.value = bucket.count;
        // Mark this bucket as manually adjusted
        const key = `${breakdown.handle}:${bucket.variantId || ''}`;
        this.manuallyAdjustedBuckets.add(key);
        this.updateCalculations(); // Full recalculation needed for quantity changes
      });

      countDiv.appendChild(decreaseBtn);
      countDiv.appendChild(countInput);
      countDiv.appendChild(increaseBtn);

      variantItem.appendChild(img);
      variantItem.appendChild(variantInfo);
      variantItem.appendChild(countDiv);
      variantsDiv.appendChild(variantItem);
    });
  }

  updateLengthDisplay() {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const lengthDisplay = document.querySelector(`${sectionPrefix} .calculator-length-display-${sectionIdOnly}`);
    if (lengthDisplay) {
      lengthDisplay.textContent = `${this.length}m`;
    }
  }

  updateAreaDisplay() {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const areaDisplay = document.querySelector(`${sectionPrefix} #calculated-area-${sectionIdOnly}`);
    if (areaDisplay) {
      const area = this.getArea();
      areaDisplay.textContent = area.toFixed(1);
    }
  }

  calculateItemBreakdown(item, area) {
    const handle = this.normalizeHandle(item.data.handle);
    const useFull = this.fullCover; // Use the full cover toggle state

    let buckets = [];
    let defaultLen = null;

    // rapid-cure-spray is calculated separately based on sealant litres in addRapidCureSprayIfNeeded()
    // Return empty buckets here - it will be added back after sealant is calculated
    if (handle === 'rapid-cure-spray') {
      return {
        handle,
        name: item.data.name,
        buckets: [],
        selected: item.selected,
        data: item.data,
        defaultLength: null,
      };
    }

    // Check if product has a multiplier (length-based product)
    const hasMultiplier = item.data.multiplier && item.data.multiplier > 0;

    if (hasMultiplier || ['geo-textile', 'seam-tape', 'brush-roller'].includes(handle)) {
      buckets = this.calculateVariants(item.data, area);
      if (hasMultiplier || handle === 'geo-textile') {
        // Calculate defaultLen based on sealant litres (area-based)
        const sealantItem = this.items.find((i) => this.normalizeHandle(i.data.handle) === 'waterproof-sealant');
        if (sealantItem) {
          let sealantLitres = 0;
          if (this.concreteRoof && sealantItem.data.coverageRough && sealantItem.data.coverageRough > 0) {
            const areaPercentage = sealantItem.data.areaPercentage || 1;
            const effectiveAreaPercentage = useFull ? 1 : areaPercentage;
            const effectiveArea = Number(area) * Number(effectiveAreaPercentage);
            sealantLitres = effectiveArea * Number(sealantItem.data.coverageRough);
            sealantLitres = Math.round(sealantLitres * 1000) / 1000;
          } else if (this.coverageRates['waterproof-sealant']) {
            sealantLitres = this.coverageRates['waterproof-sealant'](area, useFull);
          }
          // 1m per L of sealant, multiplied by area multiplier
          defaultLen = Math.round(sealantLitres * (item.data.multiplier || 1));
        } else {
          defaultLen = 0;
        }
      }
    } else {
      buckets = this.calculateBuckets(item.data, area, useFull);
    }

    // Match variants with selected colors
    buckets = this.matchVariantsWithColors(buckets, item, handle);

    return {
      handle,
      name: (() => {
        if (handle === 'waterproof-sealant') {
          return item.data.name.replace(/Liquid Rubber DIY\s*/i, '').trim();
        }
        if (handle === 'etch-primer') {
          return item.data.name
            .replace(/Liquid Rubber DIY\s*/i, '')
            .replace(/\s*-\s*Metal Surfaces/i, '')
            .trim();
        }
        return item.data.name;
      })(),
      buckets,
      selected: item.selected,
      data: item.data,
      defaultLength: defaultLen,
    };
  }

  matchVariantsWithColors(buckets, item, handle) {
    return buckets.map((b) => {
      const wanted = this.selectedColors[handle];
      // Normalize color values for comparison (case-insensitive, trimmed)
      const normalizeColor = (color) => (color || '').toString().trim().toLowerCase();
      const wantedNormalized = wanted ? normalizeColor(wanted) : null;

      // Helper to parse numeric size from variant (handles "4L", "4kg", "4", 4, etc.)
      const parseVariantSize = (variant) => {
        if (typeof variant.size === 'number') return variant.size;
        if (typeof variant.size === 'string') {
          const match = variant.size.match(/(\d+\.?\d*)/);
          return match ? Number.parseFloat(match[1]) : null;
        }
        // Try to parse from label/title - match both "L" (liters) and "kg" (kilograms)
        const label = variant.label || variant.title || '';
        const match = label.match(/(\d+\.?\d*)\s*(?:L|kg)/i);
        return match ? Number.parseFloat(match[1]) : null;
      };

      if (wantedNormalized && handle === this.normalizeHandle(item.data.handle)) {
        console.log('Matching variants with color:', {
          handle,
          wanted,
          wantedNormalized,
          bucketSize: b.size,
          bucketSizeType: typeof b.size,
          availableVariants: item.data.variants.map((v) => ({
            size: v.size,
            sizeParsed: parseVariantSize(v),
            color: v.color,
            colorNormalized: normalizeColor(v.color),
            variantId: v.variantId,
            label: v.label,
          })),
        });
      }

      // Try to find variant matching both size and color
      let variant = wantedNormalized
        ? item.data.variants.find((v) => {
            const variantColorNormalized = normalizeColor(v.color);
            const variantSize = parseVariantSize(v);
            const sizeMatches = variantSize !== null && Math.abs(variantSize - b.size) < 0.01; // Allow small floating point differences
            const colorMatches = variantColorNormalized === wantedNormalized;
            return sizeMatches && colorMatches;
          })
        : undefined;

      // Fallback: try by variantId, then by size only, then by label
      variant =
        variant ||
        (b.variantId ? item.data.variants.find((v) => v.variantId === b.variantId) : undefined) ||
        item.data.variants.find((v) => {
          const variantSize = parseVariantSize(v);
          return variantSize !== null && Math.abs(variantSize - b.size) < 0.01;
        }) ||
        (handle === 'brush-roller'
          ? item.data.variants.find((v) => (v.label || '').trim() === (b.label || '').trim())
          : undefined);

      if (!variant) {
        return { ...b, price: 0, imageUrl: '/placeholder.png', variantId: '' };
      }
      // IMPORTANT: Use b.size (numeric from calculation) not variant.size (string from product data)
      // variant.size is the variant title like "4L", but b.size is the parsed number like 4
      return {
        ...variant,
        size: b.size, // Keep the numeric size from the bucket calculation
        imageUrl: b.imageUrl || variant.imageUrl,
        count: b.count, // Keep the count from the bucket calculation
      };
    });
  }

  addRapidCureSprayIfNeeded(prelim) {
    const list = prelim.slice();
    const sprayItem = this.items.find((i) => this.normalizeHandle(i.data.handle) === 'rapid-cure-spray');

    if (!sprayItem) {
      console.warn('🚨 RAPID CURE SPRAY: sprayItem not found in items');
      return list;
    }

    console.warn('🚨 RAPID CURE SPRAY: Starting calculation');
    console.log(
      'Looking for rapid-cure-spray. Items:',
      this.items.map((i) => ({ handle: i.data.handle, normalized: this.normalizeHandle(i.data.handle) }))
    );
    console.log('Found sprayItem:', sprayItem);

    const sprayBuckets = this.calculateRapidCureSpray(sprayItem, prelim);

    console.warn('🚨 RAPID CURE SPRAY: Buckets calculated', sprayBuckets);
    console.log('Adding rapid-cure-spray to list with buckets:', sprayBuckets);
    list.push({
      handle: 'rapid-cure-spray',
      name: sprayItem.data.name,
      buckets: sprayBuckets,
      selected: sprayItem.selected,
      data: sprayItem.data,
      defaultLength: null,
    });

    return list;
  }

  calculateRapidCureSpray(sprayItem, prelim) {
    // Ensure sealant is calculated first - find it in prelim
    const sealantForSpray = prelim.find((b) => this.normalizeHandle(b.handle) === 'waterproof-sealant');

    if (!sealantForSpray) {
      console.warn('🚨 RAPID CURE SPRAY ERROR: Waterproof-sealant not found in prelim');
      console.log(
        'Available breakdowns in prelim:',
        prelim.map((b) => ({ handle: b.handle, buckets: b.buckets.length }))
      );
      return [];
    }

    if (!sealantForSpray.buckets || sealantForSpray.buckets.length === 0) {
      console.warn('🚨 RAPID CURE SPRAY ERROR: Waterproof-sealant has no buckets');
      return [];
    }

    // Calculate total sealant litres sold
    // Ensure size and count are numbers (they might be strings or undefined)
    const sprayLitresSold = sealantForSpray.buckets.reduce((sum, bk) => {
      const size = Number(bk.size) || 0;
      const count = Number(bk.count) || 0;
      const litres = size * count;
      console.log('Sealant bucket litres calculation:', {
        bucket: bk,
        size: bk.size,
        sizeAsNumber: size,
        count: bk.count,
        countAsNumber: count,
        litres,
      });
      return sum + litres;
    }, 0);

    console.log('Calculating rapid-cure-spray:', {
      sprayLitresSold,
      sealantBuckets: sealantForSpray.buckets.map((b) => `${b.count}x${b.size}L`),
      sealantTotalLitres: sprayLitresSold,
      sprayItemVariants: sprayItem.data.variants?.length || 0,
    });

    if (sprayLitresSold <= 0) {
      console.warn('🚨 RAPID CURE SPRAY ERROR: No sealant litres sold (sprayLitresSold =', sprayLitresSold, ')');
      return [];
    }

    // Rapid cure spray is calculated as a direct ratio to sealant litres
    // Default: 0.02L of rapid cure spray per 1L of waterproof sealant
    // Formula: spray_litres = sealant_litres * coverageRate
    // Parse coverage as number (might come as string from Liquid)
    const coverageRaw = sprayItem.data.coverage;
    const coverageRate =
      coverageRaw !== undefined && coverageRaw !== null && Number(coverageRaw) > 0 ? Number(coverageRaw) : 0.02;
    const eps = 1e-6; // Epsilon for floating point comparisons
    const required = Math.ceil(sprayLitresSold * coverageRate * 100) / 100;

    console.log('Rapid-cure-spray calculation:', {
      coverageRate,
      coverageRaw: sprayItem.data.coverage,
      required,
      sprayLitresSold,
      calculation: `${sprayLitresSold}L sealant * ${coverageRate} = ${required}L spray`,
    });

    if (required <= eps) {
      console.warn('🚨 RAPID CURE SPRAY ERROR: Required amount too small (required =', required, ', eps =', eps, ')');
      return [];
    }

    if (!sprayItem.data.variants || sprayItem.data.variants.length === 0) {
      console.warn('🚨 RAPID CURE SPRAY ERROR: No variants found for rapid-cure-spray product');
      return [];
    }

    const variantsWithSize = this.parseSprayVariants(sprayItem.data.variants);
    if (variantsWithSize.length === 0) {
      console.warn('🚨 RAPID CURE SPRAY ERROR: No variants could be parsed for rapid-cure-spray');
      console.log('Raw variants:', sprayItem.data.variants);
      return [];
    }

    return this.calculateSprayBuckets(required, variantsWithSize);
  }

  parseSprayVariants(variants) {
    console.log('=== parseSprayVariants START ===');
    console.log('Raw spray variants:', JSON.stringify(variants, null, 2));
    console.log('Variants count:', variants?.length || 0);

    // Expected sizes in order - used for normalization
    const expectedSizes = [0.2, 0.5, 1, 5, 20];
    const eps = 1e-3; // Tolerance for size matching (0.001L)

    const variantsWithSize = variants
      .map((v) => {
        // Try label first, then title, then size (which might be a string)
        const label = v.label || v.title || (typeof v.size === 'string' ? v.size : '') || '';
        console.log('Parsing variant:', { label, title: v.title, size: v.size, fullVariant: v });

        // Try multiple regex patterns to match different formats:
        // - "0.2L", "0.2 L", "0.2L Bottle", etc.
        // - "0.2", "0.2 litres", etc.
        let m = /(\d+(?:\.\d+)?)\s*L/i.exec(label);
        if (!m) {
          // Try without L
          m = /^(\d+(?:\.\d+)?)\s*(?:litres?|ml|mL)?$/i.exec(label.trim());
        }
        if (!m) {
          // Try extracting just the number at the start
          m = /^(\d+(?:\.\d+)?)/.exec(label.trim());
        }

        if (!m) {
          console.warn('No size match for variant:', label, 'Full variant:', v);
          return null;
        }
        const numericSize = Number.parseFloat(m[1]);

        // Normalize size to match expected sizes (handle floating point precision issues)
        // Find the closest expected size within tolerance
        let normalizedSize = numericSize;
        for (const expectedSize of expectedSizes) {
          if (Math.abs(numericSize - expectedSize) < eps) {
            normalizedSize = expectedSize;
            break;
          }
        }

        console.log('Extracted size:', numericSize, 'normalized to:', normalizedSize, 'from label:', label);
        return { ...v, size: normalizedSize };
      })
      .filter((v) => v !== null && !Number.isNaN(v.size));
    console.log('Variants with size:', variantsWithSize);
    return variantsWithSize;
  }

  calculateSprayBuckets(required, variantsWithSize) {
    if (!variantsWithSize || variantsWithSize.length === 0) {
      console.warn('No variants with size found for rapid-cure-spray');
      return [];
    }

    const byExactSize = new Map();
    variantsWithSize.forEach((v) => {
      const s = v.size;
      // Use the normalized size as key - if multiple variants have same size, keep the first one
      if (!byExactSize.has(s)) {
        byExactSize.set(s, v);
      }
    });

    console.log('Available sizes in variants:', Array.from(byExactSize.keys()));

    const sizesOrder = [0.2, 0.5, 1, 5, 20];
    const counts = { 0.2: 0, 0.5: 0, 1: 0, 5: 0, 20: 0 };
    let remaining = required;
    const eps = 1e-6;

    // Helper to add a bottle size if available; otherwise upgrade to next available size
    const addSize = (target) => {
      let idx = sizesOrder.indexOf(target);
      // Find the first available size >= target
      while (idx < sizesOrder.length && !byExactSize.has(sizesOrder[idx])) {
        idx++;
      }
      // If no size found, use the largest available size
      if (idx >= sizesOrder.length) {
        const availableSizes = sizesOrder.filter((s) => byExactSize.has(s));
        if (availableSizes.length === 0) {
          console.error('No available variants found for rapid-cure-spray');
          return;
        }
        const chosen = Math.max(...availableSizes);
        counts[chosen] = (counts[chosen] || 0) + 1;
        console.log(`No variant for ${target}L, using ${chosen}L instead`);
      } else {
        const chosen = sizesOrder[idx];
        // Only add if the size exists in byExactSize
        if (byExactSize.has(chosen)) {
          counts[chosen] = (counts[chosen] || 0) + 1;
        } else {
          console.warn(`Size ${chosen}L not found in available variants`);
        }
      }
    };

    // Use 20L only when smaller-bottle constraints cannot satisfy the remainder.
    // Max without a 20L is: 3×5L + 4×1L + 0.5L + 0.2L = 19.7L
    while (remaining > 19.7 + eps) {
      addSize(20);
      remaining -= 20;
    }

    // Use 5L while remainder exceeds what smaller sizes can cover: 4×1L + 0.5 + 0.2 = 4.7L
    while (remaining > 4.7 + eps) {
      addSize(5);
      remaining -= 5;
    }

    // Use up to four 1L bottles while remainder > 0.7L
    let onesUsed = 0;
    while (remaining > 0.7 + eps && onesUsed < 4) {
      addSize(1);
      onesUsed++;
      remaining -= 1;
    }

    // Final fractional remainder rules
    if (remaining > eps) {
      if (remaining <= 0.2 + eps) {
        addSize(0.2);
      } else if (remaining <= 0.5 + eps) {
        addSize(0.5);
      } else {
        // 0.5L + 0.2L, but never two 0.2L
        addSize(0.5);
        addSize(0.2);
      }
    }

    // Log counts and available sizes for debugging
    console.log('Spray bucket calculation summary:', {
      required,
      counts,
      availableSizes: Array.from(byExactSize.keys()),
      sizesOrder,
    });

    // Collapse counts into buckets using the chosen variant per size
    const sprayBuckets = sizesOrder
      .filter((s) => {
        const hasCount = (counts[s] || 0) > 0;
        const hasVariant = byExactSize.has(s);
        if (hasCount && !hasVariant) {
          console.warn(
            `Count > 0 for ${s}L but no variant available. Available sizes:`,
            Array.from(byExactSize.keys())
          );
        }
        return hasCount && hasVariant;
      })
      .map((s) => {
        const v = byExactSize.get(s);
        if (!v) {
          console.error(`Variant not found for size ${s}L despite check`);
          return null;
        }
        // Try to find variant with matching color if color is selected
        const wanted = this.selectedColors['rapid-cure-spray'];
        let variant = v;
        if (wanted) {
          const colorMatch = variantsWithSize.find((v) => v.size === s && v.color === wanted);
          if (colorMatch) variant = colorMatch;
        }
        // Create bucket structure matching calculateBuckets() format
        // Use s (the size from sizesOrder) as the definitive size
        const bucket = {
          size: s, // Use the size from sizesOrder, not variant.size (which might be string or number)
          count: counts[s],
          price: variant.price || 0,
          variantId: variant.variantId || variant.id || '',
          imageUrl: variant.imageUrl || variant.image || '/placeholder.png',
          label: variant.label || variant.title || `${s}L Bottle`,
          color: variant.color || '',
        };
        console.log('Created spray bucket:', bucket);
        return bucket;
      })
      .filter(Boolean);

    console.log('Final sprayBuckets:', sprayBuckets);
    console.log('SprayBuckets length:', sprayBuckets.length);

    if (sprayBuckets.length === 0 && required > 0) {
      console.warn('🚨 RAPID CURE SPRAY ERROR: No spray buckets created despite required > 0', {
        required,
        counts,
        availableSizes: Array.from(byExactSize.keys()),
        variantsWithSizeCount: variantsWithSize.length,
      });
    }

    return sprayBuckets;
  }

  organizeBreakdowns(list) {
    // Ensure brush-roller is last
    // Exclude liquid-rubber-diy-2-piece-brush-kit (it's in bonus, not calculator)
    // Include rapid-cure-spray, sealer, and etch-primer even if they have no buckets
    // (sealer and etch-primer need to be always available for toggling)
    // Also include products with multipliers (length-based products like Grit) even if they have no buckets
    // Products with multiplier property (even if 0) are configured products that should always show
    const withBuckets = list.filter((b) => {
      const hasBuckets = b.buckets && b.buckets.length > 0;
      const isSpecialProduct = b.handle === 'rapid-cure-spray' || b.handle === 'sealer' || b.handle === 'etch-primer';
      const hasMultiplier = b.data && 'multiplier' in b.data;
      return (hasBuckets || isSpecialProduct || hasMultiplier) && b.handle !== 'liquid-rubber-diy-2-piece-brush-kit';
    });
    const nonBonus = withBuckets.filter((b) => b.handle !== 'brush-roller');
    const bonus = withBuckets.filter((b) => b.handle === 'brush-roller');
    return nonBonus.concat(bonus);
  }

  /**
   * Preserve color swatches selected state before clearing container
   * @param {HTMLElement} container - The product breakdown container
   * @returns {Map<string, string>} Map of handle to selected color value
   */
  preserveColorSwatches(container) {
    const preservedSelectedColors = new Map();
    const existingProductCards = Array.from(container.querySelectorAll('.product-card'));

    existingProductCards.forEach((card) => {
      const handle = card.dataset.handle;
      if (handle) {
        const swatchesContainer = card.querySelector('.product-color-swatches-container');
        if (swatchesContainer) {
          const selectedInput = swatchesContainer.querySelector('input[type="radio"]:checked');
          const selectedColor = selectedInput ? selectedInput.value : null;
          if (selectedColor) {
            // Use normalizedHandle as key so lookup in createColorSwatchesSection/createCustomColorSwatchesSection works
            const normalizedHandle = this.normalizeHandle(handle);
            preservedSelectedColors.set(normalizedHandle, selectedColor);
          }
        }
      }
    });

    return preservedSelectedColors;
  }

  /**
   * Set up click-outside handler for tooltips
   */
  setupTooltipClickHandler() {
    if (this.tooltipClickHandler) return;

    this.tooltipClickHandler = (e) => {
      const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
      const allTooltipContainers = document.querySelectorAll(
        `${sectionPrefix} .product-why-tooltip-container, ${sectionPrefix} .toggle-tooltip-container`
      );
      allTooltipContainers.forEach((tooltipContainer) => {
        if (!tooltipContainer.contains(e.target)) {
          tooltipContainer.classList.remove('active');
        }
      });
    };
    document.addEventListener('click', this.tooltipClickHandler, true);
  }

  /**
   * Create product image column with clickable image wrapper
   * @param {Object} bd - Breakdown data
   * @returns {HTMLElement} Product column element
   */
  createProductImageColumn(bd) {
    const productColumn = document.createElement('div');
    productColumn.className = 'product-card-column';

    const imageUrl =
      bd.buckets.length > 0
        ? bd.buckets[0].imageUrl || '/placeholder.png'
        : bd.data.featured_image || '/placeholder.png';

    const imageWrapper = document.createElement('button');
    imageWrapper.type = 'button';
    imageWrapper.className = 'product-card-image-wrapper';
    imageWrapper.setAttribute('aria-label', `View ${bd.name} options`);
    imageWrapper.dataset.productHandle = bd.data.handle;
    imageWrapper.dataset.productId = bd.data.id;
    imageWrapper.dataset.productUrl = `/products/${bd.data.handle}`;

    const img = document.createElement('img');
    img.src = imageUrl;
    img.alt = bd.name;
    img.className = 'product-card-image';
    img.dataset.productHandle = bd.handle;
    imageWrapper.appendChild(img);

    const spinner = document.createElement('div');
    spinner.className = 'loading__spinner hidden';
    const existingSpinner = document.querySelector('.loading__spinner');
    if (existingSpinner) {
      spinner.innerHTML = existingSpinner.innerHTML;
    } else {
      spinner.innerHTML =
        '<svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg"><circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle></svg>';
    }
    imageWrapper.appendChild(spinner);

    imageWrapper.addEventListener('click', (e) => {
      e.preventDefault();
      this.openQuickAddModal(bd.data.handle, bd.data.id, imageWrapper);
    });

    productColumn.appendChild(imageWrapper);
    return productColumn;
  }

  /**
   * Create "Why do I need?" tooltip if whyText is available
   * @param {Object} bd - Breakdown data
   * @param {HTMLElement} titleColumn - Title column to append tooltip to
   */
  createWhyTooltip(bd, titleColumn) {
    if (!bd.data.whyText || !bd.data.whyText.trim()) return;

    const whyTooltipContainer = document.createElement('div');
    whyTooltipContainer.className = 'product-why-tooltip-container';

    const whyTrigger = document.createElement('button');
    whyTrigger.className = 'product-why-trigger';
    whyTrigger.type = 'button';
    whyTrigger.setAttribute('aria-label', 'Why do I need this product?');
    whyTrigger.innerHTML = 'Why do I need? <span class="why-icon">i</span>';

    const whyTooltip = document.createElement('div');
    whyTooltip.className = 'product-why-tooltip';
    whyTooltip.textContent = bd.data.whyText;

    whyTooltipContainer.appendChild(whyTrigger);
    whyTooltipContainer.appendChild(whyTooltip);

    whyTrigger.addEventListener('click', (e) => {
      e.stopPropagation();
      const isActive = whyTooltipContainer.classList.contains('active');
      const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
      const allTooltipContainers = document.querySelectorAll(`${sectionPrefix} .product-why-tooltip-container`);
      allTooltipContainers.forEach((container) => {
        if (container !== whyTooltipContainer) {
          container.classList.remove('active');
        }
      });
      if (isActive) {
        whyTooltipContainer.classList.remove('active');
      } else {
        whyTooltipContainer.classList.add('active');
      }
    });

    titleColumn.appendChild(whyTooltipContainer);
  }

  /**
   * Create color swatches section for products with color variants
   * This is a complex method that handles color selection UI
   * @param {Object} bd - Breakdown data
   * @param {Map<string, string>} preservedSelectedColors - Map of preserved colors
   * @param {HTMLElement} titleColumn - Title column to append to
   */
  createColorSwatchesSection(bd, preservedSelectedColors, titleColumn) {
    if (!bd.data.hasColorVariants || !bd.data.colorSwatchesHtml) return;

    const normalizedHandle = this.normalizeHandle(bd.handle);
    const isProtectiveTopCoat = normalizedHandle === 'protective-top-coat';
    const isToughCoat = normalizedHandle === 'tough-coat';
    const isWaterproofSealant = normalizedHandle === 'waterproof-sealant';

    const colorSwatchesAndToggleContainer = document.createElement('div');
    colorSwatchesAndToggleContainer.className = 'product-color-swatches-and-toggle-container';
    colorSwatchesAndToggleContainer.style.display = 'flex';
    colorSwatchesAndToggleContainer.style.alignItems = 'flex-start';
    colorSwatchesAndToggleContainer.style.gap = '1.5rem';

    const colorSwatchesWrapper = document.createElement('div');
    colorSwatchesWrapper.className = 'product-color-swatches-wrapper';

    const colorLabelAbove = document.createElement('div');
    colorLabelAbove.className = 'product-color-label-above';
    colorLabelAbove.style.fontWeight = 'bold';
    colorLabelAbove.style.color = '#000000';
    colorLabelAbove.style.marginBottom = '0';
    colorLabelAbove.style.fontSize = '1rem';
    colorLabelAbove.textContent = isProtectiveTopCoat || isToughCoat ? 'COLORBOND®: ' : 'Color: ';

    let colorLabelBelow = null;
    if (isProtectiveTopCoat) {
      colorLabelBelow = document.createElement('div');
      colorLabelBelow.className = 'product-color-label-below';
      colorLabelBelow.style.color = '#666666';
      colorLabelBelow.style.marginTop = '0';
      colorLabelBelow.style.fontSize = '0.875rem';
      colorLabelBelow.textContent = 'Recommended colour: White';
    }

    const colorSwatchesContainer = document.createElement('div');
    colorSwatchesContainer.className = 'product-color-swatches-container';
    colorSwatchesContainer.innerHTML = bd.data.colorSwatchesHtml;

    const preservedColor = preservedSelectedColors.get(normalizedHandle);
    if (preservedColor) {
      colorSwatchesContainer.dataset.preservedColor = preservedColor;
    }

    const swatchesElement = colorSwatchesContainer.querySelector('.custom-color-swatches');
    if (swatchesElement) {
      swatchesElement.dataset.productId = bd.data.id;
      swatchesElement.dataset.calculatorProductId = `${this.sectionId}-${bd.data.id}`;

      const inputs = colorSwatchesContainer.querySelectorAll('input[type="radio"]');
      const labels = colorSwatchesContainer.querySelectorAll('label');
      const preservedColorValue = colorSwatchesContainer.dataset.preservedColor;
      const selectedColor = preservedColorValue || this.selectedColors[normalizedHandle];

      if (preservedColorValue) {
        this.selectedColors[normalizedHandle] = preservedColorValue;
      }

      const updateColorLabels = (colorName) => {
        if (colorName) {
          const formattedColorName = colorName
            .split(' ')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
          if (isProtectiveTopCoat) {
            colorLabelAbove.textContent = `COLORBOND®: ${formattedColorName}`;
          } else {
            colorLabelAbove.textContent = `Color: ${formattedColorName}`;
          }
        } else if (isProtectiveTopCoat) {
          colorLabelAbove.textContent = 'COLORBOND®: ';
        } else {
          colorLabelAbove.textContent = 'Color: ';
        }
      };

      let initialColorName = '';
      if (selectedColor) {
        const selectedInput = Array.from(inputs).find((input) => {
          const inputColor = (input.value || '').toString().trim().toLowerCase();
          const selectedColorNormalized = selectedColor.toString().trim().toLowerCase();
          return inputColor === selectedColorNormalized;
        });
        if (selectedInput) {
          initialColorName = selectedInput.value || selectedInput.getAttribute('title') || selectedColor;
        } else {
          initialColorName = selectedColor;
        }
      } else {
        const firstInput = inputs[0];
        if (firstInput) {
          initialColorName = firstInput.value || firstInput.getAttribute('title') || '';
        }
      }
      updateColorLabels(initialColorName);

      inputs.forEach((input, index) => {
        const oldId = input.id;
        const newId = `${oldId}-${this.sectionId}-${bd.data.id}`;
        input.id = newId;
        if (labels[index]) {
          labels[index].setAttribute('for', newId);
        }
        const dataIndex = input.dataset.index || '';
        input.name = `option-${this.sectionId}-${bd.data.id}-${dataIndex}`;

        if (selectedColor) {
          const inputColor = (input.value || '').toString().trim().toLowerCase();
          const selectedColorNormalized = selectedColor.toString().trim().toLowerCase();
          if (inputColor === selectedColorNormalized) {
            input.checked = true;
          } else {
            input.checked = false;
          }
        }
      });

      const swatchesContainer = colorSwatchesContainer.querySelector('.custom-color-swatches__container');
      let dropdown = null;
      let updateAllSwatchSelection = null;

      if (swatchesContainer) {
        const allSwatches = Array.from(swatchesContainer.querySelectorAll('.custom-color-swatch'));
        const maxVisible = 4;

        updateAllSwatchSelection = this.createUpdateAllSwatchSelectionFunction(swatchesContainer, null);

        if (allSwatches.length > maxVisible) {
          allSwatches.forEach((swatch, index) => {
            if (index >= maxVisible) {
              swatch.classList.add('color-swatch-hidden');
              swatch.style.display = 'none';
            }
          });

          const showMoreContainer = document.createElement('div');
          showMoreContainer.className = 'color-swatches-show-more-container';

          const showMoreButton = document.createElement('button');
          showMoreButton.type = 'button';
          showMoreButton.className = 'color-swatches-show-more-btn';
          showMoreButton.textContent = 'Show more';
          showMoreButton.setAttribute('aria-expanded', 'false');
          showMoreButton.setAttribute('aria-label', 'Show more color options');

          dropdown = document.createElement('div');
          dropdown.className = 'color-swatches-dropdown';
          dropdown.style.display = 'none';

          updateAllSwatchSelection = this.createUpdateAllSwatchSelectionFunction(swatchesContainer, dropdown);

          const hiddenSwatches = allSwatches.slice(maxVisible);
          hiddenSwatches.forEach((swatch) => {
            const dropdownRow = document.createElement('div');
            dropdownRow.className = 'color-swatches-dropdown-row';

            const clonedSwatch = swatch.cloneNode(true);
            clonedSwatch.style.display = 'inline-block';
            clonedSwatch.classList.remove('color-swatch-hidden', 'custom-color-swatch--selected');
            const clonedLabel = clonedSwatch.querySelector('.custom-color-swatch__label');
            if (clonedLabel) {
              clonedLabel.classList.remove('custom-color-swatch--selected');
            }
            const clonedInput = clonedSwatch.querySelector('input[type="radio"]');
            if (clonedInput) {
              clonedInput.checked = false;
              clonedInput.removeAttribute('checked');
            }

            const colorName = swatch.getAttribute('title') || swatch.dataset.value || '';
            const clonedTooltip = clonedSwatch.querySelector('.custom-color-swatch__tooltip');
            if (!clonedTooltip && colorName) {
              const tooltipElement = document.createElement('div');
              tooltipElement.className = 'custom-color-swatch__tooltip';
              tooltipElement.textContent = colorName.toUpperCase().trim();
              clonedSwatch.appendChild(tooltipElement);
            }

            const colorNameSpan = document.createElement('span');
            colorNameSpan.className = 'color-swatches-dropdown-name';
            colorNameSpan.textContent = colorName;

            dropdownRow.appendChild(clonedSwatch);
            dropdownRow.appendChild(colorNameSpan);

            this.handleDropdownRowClick({
              dropdownRow,
              clonedSwatch,
              swatch,
              swatchesContainer,
              dropdown,
              showMoreButton,
              productHandle: bd.handle,
              isProtectiveTopCoat,
              updateAllSwatchSelection,
            });

            dropdown.appendChild(dropdownRow);
          });

          showMoreButton.addEventListener('click', (e) => {
            e.stopPropagation();
            const isExpanded = showMoreButton.getAttribute('aria-expanded') === 'true';
            if (isExpanded) {
              dropdown.style.display = 'none';
              showMoreButton.setAttribute('aria-expanded', 'false');
              showMoreButton.textContent = 'Show more';
            } else {
              dropdown.style.display = 'block';
              showMoreButton.setAttribute('aria-expanded', 'true');
              showMoreButton.textContent = 'Show less';
            }
          });

          document.addEventListener('click', (e) => {
            if (!showMoreContainer.contains(e.target)) {
              dropdown.style.display = 'none';
              showMoreButton.setAttribute('aria-expanded', 'false');
              showMoreButton.textContent = 'Show more';
            }
          });

          showMoreContainer.appendChild(showMoreButton);
          showMoreContainer.appendChild(dropdown);
          swatchesContainer.appendChild(showMoreContainer);
        }

        const visibleSwatches = swatchesContainer.querySelectorAll('.custom-color-swatch');
        visibleSwatches.forEach((swatch) => {
          const input = swatch.querySelector('input[type="radio"]');
          const label = swatch.querySelector('label.custom-color-swatch__label');

          if (!input || input.disabled) return;

          let tooltipText = swatch.getAttribute('title') || swatch.dataset.value || input.value || '';
          if (tooltipText) {
            tooltipText = tooltipText.toUpperCase().trim();
            let tooltipElement = swatch.querySelector('.custom-color-swatch__tooltip');
            if (tooltipElement) {
              tooltipElement.textContent = tooltipText;
            } else {
              tooltipElement = document.createElement('div');
              tooltipElement.className = 'custom-color-swatch__tooltip';
              tooltipElement.textContent = tooltipText;
              swatch.appendChild(tooltipElement);
            }
            swatch.dataset.tooltip = tooltipText;
            swatch.removeAttribute('title');
            if (label) {
              label.removeAttribute('title');
              label.style.pointerEvents = 'auto';
            }
          }

          const handleColorSelection = (e) => {
            this.handleSwatchColorSelection(e, {
              input,
              swatch,
              swatchesContainer,
              productHandle: bd.handle,
              isProtectiveTopCoat,
              isToughCoat,
              updateColorLabels,
            });
          };

          swatch.addEventListener('click', handleColorSelection, true);
          if (label) {
            label.addEventListener('click', handleColorSelection, true);
          }
        });

        this.setupSwatchChangeListener(
          swatchesContainer,
          bd.handle,
          isProtectiveTopCoat,
          isToughCoat,
          updateAllSwatchSelection,
          updateColorLabels
        );

        const swatchesElementForEvent = colorSwatchesContainer.querySelector('.custom-color-swatches');
        if (swatchesElementForEvent) {
          swatchesElementForEvent.addEventListener('custom-color-swatch:changed', (e) => {
            const detail = e.detail;
            if (detail?.value) {
              const normalizedHandleForEvent = this.normalizeHandle(bd.handle);
              this.selectedColors[normalizedHandleForEvent] = detail.value;
              updateColorLabels(detail.value);
              this.updateProductVariants(bd.handle);
              const isToughCoatForEvent = normalizedHandleForEvent === 'tough-coat';
              if (isProtectiveTopCoat || isToughCoatForEvent) {
                this.updateProductImageForColor(bd.handle, detail.value);
              }
            }
          });
        }

        updateAllSwatchSelection();
      }

      colorSwatchesWrapper.appendChild(colorLabelAbove);
      colorSwatchesWrapper.appendChild(colorSwatchesContainer);
      if (colorLabelBelow) {
        colorSwatchesWrapper.appendChild(colorLabelBelow);
      }

      colorSwatchesAndToggleContainer.appendChild(colorSwatchesWrapper);

      if (isWaterproofSealant && this.showFullCoverToggle) {
        const fullCoverToggle = this.createFullCoverToggle();
        colorSwatchesAndToggleContainer.appendChild(fullCoverToggle);
      }

      titleColumn.appendChild(colorSwatchesAndToggleContainer);
    }
  }

  /**
   * Create custom color swatches section for products without color variants
   * Uses same kit-builder style (COLORBOND/Color label, square swatches, Show more)
   * Selected color is stored and added as line item property when adding to cart
   * @param {Object} bd - Breakdown data
   * @param {HTMLElement} titleColumn - Title column to append to
   * @param {Map<string, string>} [preservedSelectedColors] - Map of preserved colors from previous render
   */
  createCustomColorSwatchesSection(bd, titleColumn, preservedSelectedColors) {
    const normalizedHandle = this.normalizeHandle(bd.handle);
    const customColors = bd.data.customColors || [];

    const preservedColor = preservedSelectedColors && preservedSelectedColors.get(normalizedHandle);
    const initialColor = preservedColor || (customColors[0] ? customColors[0].name : '');
    if (initialColor) {
      this.selectedColors[normalizedHandle] = initialColor;
    }

    const colorSwatchesAndToggleContainer = document.createElement('div');
    colorSwatchesAndToggleContainer.className = 'product-color-swatches-and-toggle-container';
    colorSwatchesAndToggleContainer.style.display = 'flex';
    colorSwatchesAndToggleContainer.style.alignItems = 'flex-start';
    colorSwatchesAndToggleContainer.style.gap = '1.5rem';

    const colorSwatchesWrapper = document.createElement('div');
    colorSwatchesWrapper.className = 'product-color-swatches-wrapper';

    const colorLabelAbove = document.createElement('div');
    colorLabelAbove.className = 'product-color-label-above';
    colorLabelAbove.style.fontWeight = 'bold';
    colorLabelAbove.style.color = '#000000';
    colorLabelAbove.style.marginBottom = '0';
    colorLabelAbove.style.fontSize = '1rem';
    colorLabelAbove.textContent = 'Color: ';

    const colorSwatchesContainer = document.createElement('div');
    colorSwatchesContainer.className = 'product-color-swatches-container';

    const swatchesWrapper = document.createElement('div');
    swatchesWrapper.className = 'custom-color-swatches custom-color-swatches--mini';
    swatchesWrapper.dataset.productId = bd.data.id;
    swatchesWrapper.dataset.calculatorProductId = `${this.sectionId}-${bd.data.id}`;
    swatchesWrapper.dataset.customColorSwatch = 'true';

    const swatchesContainer = document.createElement('div');
    swatchesContainer.className = 'custom-color-swatches__container';

    const colorNameToHex = {
      white: '#FFFFFF', black: '#000000', red: '#FF0000', blue: '#0000FF', green: '#008000',
      grey: '#808080', gray: '#808080', silver: '#C0C0C0', gold: '#FFD700', cream: '#FFFDD0',
      beige: '#F5F5DC', surfmist: '#e4e2d5', 'dover-white': '#f9fbf1', 'evening-haze': '#c5c2aa',
      southerly: '#d2d1cb', dune: '#b1ada3', paperbark: '#cabfa4', 'classic-cream': '#e9dcb8',
      'shale-grey': '#bdbfba', bluegum: '#969799', monument: '#323233', 'night-sky': '#000000',
      ironstone: '#3e434c', 'deep-ocean': '#364152', 'cottage-green': '#304c3c',
    };
    const toHex = (h, name) => {
      if (h && h.startsWith('#')) return h;
      const key = (h || name || '').toString().trim().toLowerCase().replace(/\s+/g, '-');
      return colorNameToHex[key] || `#${(h || key).toString().replace(/^#/, '')}`;
    };

    const maxVisible = 4;
    const initialColorNorm = (initialColor || '').toString().trim().toLowerCase();
    customColors.forEach((color, index) => {
      const hex = toHex(color.hex, color.name);
      const isSelected = (color.name || '').toString().trim().toLowerCase() === initialColorNorm || (index === 0 && !initialColorNorm);
      const swatchDiv = document.createElement('div');
      swatchDiv.className = `custom-color-swatch${isSelected ? ' custom-color-swatch--selected' : ''}`;
      swatchDiv.dataset.value = color.name;
      swatchDiv.title = color.name;
      if (index >= maxVisible) {
        swatchDiv.classList.add('color-swatch-hidden');
        swatchDiv.style.display = 'none';
      }

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `custom-color-${this.sectionId}-${bd.data.id}`;
      input.value = color.name;
      input.id = `custom-swatch-${this.sectionId}-${bd.data.id}-${index}`;
      input.className = 'custom-color-swatch__input';
      input.checked = isSelected;
      input.dataset.variantId = '';

      const label = document.createElement('label');
      label.className = 'custom-color-swatch__label';
      label.htmlFor = input.id;
      label.style.cssText = `background-color:${hex};${/fff|f9f|e4e|f5f5dc|fffdd0/i.test(hex) ? 'border:1px solid #ddd;' : ''}`;

      swatchDiv.appendChild(input);
      swatchDiv.appendChild(label);
      swatchesContainer.appendChild(swatchDiv);
    });

    swatchesWrapper.appendChild(swatchesContainer);
    colorSwatchesContainer.appendChild(swatchesWrapper);
    colorSwatchesWrapper.appendChild(colorLabelAbove);
    colorSwatchesWrapper.appendChild(colorSwatchesContainer);

    const updateColorLabel = (colorName) => {
      colorLabelAbove.textContent = colorName ? `Color: ${colorName}` : 'Color: ';
    };
    updateColorLabel(initialColor);

    const handleSelect = (colorName) => {
      this.selectedColors[normalizedHandle] = colorName;
      updateColorLabel(colorName);
      swatchesContainer.querySelectorAll('.custom-color-swatch').forEach((s) => s.classList.remove('custom-color-swatch--selected'));
      swatchesContainer.querySelectorAll('input:checked').forEach((inp) => {
        const sw = inp.closest('.custom-color-swatch');
        if (sw) sw.classList.add('custom-color-swatch--selected');
      });
    };

    swatchesContainer.querySelectorAll('.custom-color-swatch').forEach((swatch) => {
      const input = swatch.querySelector('input');
      const label = swatch.querySelector('label');
      const onSelect = () => handleSelect(input.value);
      swatch.addEventListener('click', (e) => {
        if (e.target === input) return;
        e.preventDefault();
        input.checked = true;
        onSelect();
      });
      input.addEventListener('change', onSelect);
    });

    if (customColors.length > 4) {
      const showMoreContainer = document.createElement('div');
      showMoreContainer.className = 'color-swatches-show-more-container';
      const showMoreBtn = document.createElement('button');
      showMoreBtn.type = 'button';
      showMoreBtn.className = 'color-swatches-show-more-btn';
      showMoreBtn.textContent = 'Show more';
      showMoreBtn.setAttribute('aria-expanded', 'false');
      const dropdown = document.createElement('div');
      dropdown.className = 'color-swatches-dropdown';
      dropdown.style.display = 'none';

      customColors.slice(4).forEach((color) => {
        const row = document.createElement('div');
        row.className = 'color-swatches-dropdown-row';
        const hex = toHex(color.hex, color.name);
        const swatchDiv = document.createElement('div');
        swatchDiv.className = 'custom-color-swatch';
        swatchDiv.dataset.value = color.name;
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `custom-color-${this.sectionId}-${bd.data.id}`;
        input.value = color.name;
        input.className = 'custom-color-swatch__input';
        const label = document.createElement('label');
        label.className = 'custom-color-swatch__label';
        label.style.cssText = `background-color:${hex};${/fff|f9f|e4e/i.test(hex) ? 'border:1px solid #ddd;' : ''}`;
        swatchDiv.appendChild(input);
        swatchDiv.appendChild(label);
        const nameSpan = document.createElement('span');
        nameSpan.className = 'color-swatches-dropdown-name';
        nameSpan.textContent = color.name;
        row.appendChild(swatchDiv);
        row.appendChild(nameSpan);
        row.addEventListener('click', () => {
          input.checked = true;
          handleSelect(color.name);
          dropdown.style.display = 'none';
          showMoreBtn.setAttribute('aria-expanded', 'false');
          showMoreBtn.textContent = 'Show more';
        });
        dropdown.appendChild(row);
      });

      showMoreBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        const isOpen = showMoreBtn.getAttribute('aria-expanded') === 'true';
        dropdown.style.display = isOpen ? 'none' : 'block';
        showMoreBtn.setAttribute('aria-expanded', !isOpen);
        showMoreBtn.textContent = isOpen ? 'Show more' : 'Show less';
      });
      document.addEventListener('click', (e) => {
        if (!showMoreContainer.contains(e.target)) {
          dropdown.style.display = 'none';
          showMoreBtn.setAttribute('aria-expanded', 'false');
          showMoreBtn.textContent = 'Show more';
        }
      });
      showMoreContainer.appendChild(showMoreBtn);
      showMoreContainer.appendChild(dropdown);
      swatchesContainer.appendChild(showMoreContainer);
    }

    colorSwatchesAndToggleContainer.appendChild(colorSwatchesWrapper);
    titleColumn.appendChild(colorSwatchesAndToggleContainer);
  }

  /**
   * Create full cover toggle for waterproof sealant
   * @returns {HTMLElement} Full cover toggle container
   */
  createFullCoverToggle() {
    const fullCoverToggleContainer = document.createElement('div');
    fullCoverToggleContainer.className = 'product-full-cover-toggle-container';
    fullCoverToggleContainer.style.display = 'flex';
    fullCoverToggleContainer.style.flexDirection = 'column';
    fullCoverToggleContainer.style.alignItems = 'flex-start';
    fullCoverToggleContainer.style.gap = '0.5rem';

    const toggleLabelWrapper = document.createElement('div');
    toggleLabelWrapper.className = 'product-full-cover-toggle-label-wrapper';
    toggleLabelWrapper.style.display = 'flex';
    toggleLabelWrapper.style.alignItems = 'center';
    toggleLabelWrapper.style.gap = '0.5rem';

    const toggleLabel = document.createElement('span');
    toggleLabel.className = 'product-full-cover-toggle-label';
    toggleLabel.style.fontSize = '1rem';
    toggleLabel.style.fontWeight = '500';
    toggleLabel.style.color = '#000000';
    toggleLabel.textContent = this.fullCoverToggleLabel;

    toggleLabelWrapper.appendChild(toggleLabel);

    if (this.fullCoverTooltipText) {
      const tooltipContainer = document.createElement('div');
      tooltipContainer.className = 'product-full-cover-toggle-tooltip-container';
      tooltipContainer.style.position = 'relative';
      tooltipContainer.style.display = 'inline-flex';

      const tooltipTrigger = document.createElement('button');
      tooltipTrigger.type = 'button';
      tooltipTrigger.className = 'product-full-cover-toggle-tooltip-trigger';
      tooltipTrigger.setAttribute('aria-label', 'Toggle information');
      tooltipTrigger.style.display = 'inline-flex';
      tooltipTrigger.style.alignItems = 'center';
      tooltipTrigger.style.justifyContent = 'center';
      tooltipTrigger.style.width = '1.125rem';
      tooltipTrigger.style.height = '1.125rem';
      tooltipTrigger.style.padding = '0';
      tooltipTrigger.style.backgroundColor = 'transparent';
      tooltipTrigger.style.border = '1px solid #16a34a';
      tooltipTrigger.style.borderRadius = '50%';
      tooltipTrigger.style.color = '#16a34a';
      tooltipTrigger.style.fontSize = '0.75rem';
      tooltipTrigger.style.fontWeight = '700';
      tooltipTrigger.style.lineHeight = '1';
      tooltipTrigger.style.cursor = 'pointer';
      tooltipTrigger.textContent = 'i';

      const tooltip = document.createElement('div');
      tooltip.className = 'product-full-cover-toggle-tooltip';
      tooltip.textContent = this.fullCoverTooltipText;

      tooltipTrigger.addEventListener('click', (e) => {
        e.stopPropagation();
        const isActive = tooltipContainer.classList.contains('active');
        const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
        const allTooltipContainers = document.querySelectorAll(
          `${sectionPrefix} .product-why-tooltip-container, ${sectionPrefix} .product-full-cover-toggle-tooltip-container`
        );
        allTooltipContainers.forEach((container) => {
          if (container !== tooltipContainer) {
            container.classList.remove('active');
          }
        });
        if (isActive) {
          tooltipContainer.classList.remove('active');
        } else {
          tooltipContainer.classList.add('active');
        }
      });

      tooltipContainer.appendChild(tooltipTrigger);
      tooltipContainer.appendChild(tooltip);
      toggleLabelWrapper.appendChild(tooltipContainer);
    }

    const toggleWrapper = document.createElement('label');
    toggleWrapper.className = 'roof-type-toggle';
    toggleWrapper.style.position = 'relative';
    toggleWrapper.style.display = 'inline-block';
    toggleWrapper.style.width = '4rem';
    toggleWrapper.style.height = '2rem';
    toggleWrapper.style.cursor = 'pointer';
    toggleWrapper.setAttribute('for', `full-cover-toggle-${this.sectionIdOnly}`);

    const toggleInput = document.createElement('input');
    toggleInput.type = 'checkbox';
    toggleInput.id = `full-cover-toggle-${this.sectionIdOnly}`;
    toggleInput.className = 'roof-type-toggle-input';
    toggleInput.setAttribute('aria-label', 'Toggle full cover');
    toggleInput.dataset.fullCover = 'false';
    toggleInput.dataset.calculatorId = this.sectionIdOnly;
    toggleInput.checked = this.fullCover;

    toggleInput.addEventListener('change', () => this.handleFullCoverToggle());

    const toggleSlider = document.createElement('span');
    toggleSlider.className = 'roof-type-toggle-slider';

    toggleWrapper.appendChild(toggleInput);
    toggleWrapper.appendChild(toggleSlider);

    fullCoverToggleContainer.appendChild(toggleLabelWrapper);
    fullCoverToggleContainer.appendChild(toggleWrapper);

    return fullCoverToggleContainer;
  }

  renderProductBreakdown() {
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const container = document.querySelector(`#product-breakdown-${sectionIdOnly}`);
    if (!container) return;

    const preservedSelectedColors = this.preserveColorSwatches(container);
    container.innerHTML = '';
    this.setupTooltipClickHandler();

    this.breakdowns.forEach((bd) => {
      // Always render sealer and etch-primer, but hide them if not visible
      // This ensures they exist in the DOM when toggled
      const shouldRender = this.isVisible(bd.handle) || bd.handle === 'sealer' || bd.handle === 'etch-primer';
      if (!shouldRender) return;

      // Show rapid-cure-spray even if it has no buckets initially (it's calculated based on sealant)
      // It will show up once sealant is calculated

      const card = document.createElement('div');
      card.className = 'product-card';
      card.dataset.handle = bd.handle;

      // Hide sealer/etch-primer if not visible (but still render them)
      if ((bd.handle === 'sealer' || bd.handle === 'etch-primer') && !this.isVisible(bd.handle)) {
        card.style.display = 'none';
      }

      // Always keep products selected
      bd.selected = true;

      // Create two-column layout container
      const cardContent = document.createElement('div');
      cardContent.className = 'product-card-content';

      const productColumn = this.createProductImageColumn(bd);
      cardContent.appendChild(productColumn);

      // Middle column: Product title only
      const titleColumn = document.createElement('div');
      titleColumn.className = 'product-card-title-column';

      const name = document.createElement('div');
      name.className = 'product-card-name';
      name.textContent = bd.name;
      titleColumn.appendChild(name);

      this.createWhyTooltip(bd, titleColumn);

      // Add color swatches if product has color variants
      if (bd.data.hasColorVariants && bd.data.colorSwatchesHtml) {
        // Check if this is the protective top coat or tough coat product
        const normalizedHandle = this.normalizeHandle(bd.handle);
        const isProtectiveTopCoat = normalizedHandle === 'protective-top-coat';
        const isToughCoat = normalizedHandle === 'tough-coat';
        const isWaterproofSealant = normalizedHandle === 'waterproof-sealant';

        // Create a container for color swatches and full cover toggle (side by side)
        const colorSwatchesAndToggleContainer = document.createElement('div');
        colorSwatchesAndToggleContainer.className = 'product-color-swatches-and-toggle-container';
        colorSwatchesAndToggleContainer.style.display = 'flex';
        colorSwatchesAndToggleContainer.style.alignItems = 'flex-start';
        colorSwatchesAndToggleContainer.style.gap = '1.5rem';

        // Create wrapper for color swatches with text above and below
        const colorSwatchesWrapper = document.createElement('div');
        colorSwatchesWrapper.className = 'product-color-swatches-wrapper';

        // Text above swatches: "COLORBOND®: [Color Name]" for protective top coat, "Color: [Color Name]" for others
        const colorLabelAbove = document.createElement('div');
        colorLabelAbove.className = 'product-color-label-above';
        colorLabelAbove.style.fontWeight = 'bold';
        colorLabelAbove.style.color = '#000000';
        colorLabelAbove.style.marginBottom = '0';
        colorLabelAbove.style.fontSize = '1rem';
        colorLabelAbove.textContent = isProtectiveTopCoat || isToughCoat ? 'COLORBOND®: ' : 'Color: ';

        // Text below swatches: "Recommended colour: White" (static) - only for protective top coat
        let colorLabelBelow = null;
        if (isProtectiveTopCoat) {
          colorLabelBelow = document.createElement('div');
          colorLabelBelow.className = 'product-color-label-below';
          colorLabelBelow.style.color = '#666666';
          colorLabelBelow.style.marginTop = '0';
          colorLabelBelow.style.fontSize = '0.875rem';
          colorLabelBelow.textContent = 'Recommended colour: White';
        }

        const colorSwatchesContainer = document.createElement('div');
        colorSwatchesContainer.className = 'product-color-swatches-container';

        // Use the HTML from data (swatches will be re-initialized)
        colorSwatchesContainer.innerHTML = bd.data.colorSwatchesHtml;

        // Restore preserved selected color if it exists
        const preservedColor = preservedSelectedColors.get(normalizedHandle);

        // If we have a preserved color, we'll restore it after the swatches are set up
        // Store it for later use
        if (preservedColor) {
          colorSwatchesContainer.dataset.preservedColor = preservedColor;
        }

        // Update product ID in the color swatches to match this calculator instance
        const swatchesElement = colorSwatchesContainer.querySelector('.custom-color-swatches');
        if (swatchesElement) {
          swatchesElement.dataset.productId = bd.data.id;
          swatchesElement.dataset.calculatorProductId = `${this.sectionId}-${bd.data.id}`;

          // Update all input IDs and labels to be unique for this calculator instance
          const inputs = colorSwatchesContainer.querySelectorAll('input[type="radio"]');
          const labels = colorSwatchesContainer.querySelectorAll('label');
          const normalizedHandle = this.normalizeHandle(bd.handle);

          // Use preserved color if available, otherwise use selectedColor from state
          const preservedColor = colorSwatchesContainer.dataset.preservedColor;
          const selectedColor = preservedColor || this.selectedColors[normalizedHandle];

          // Update selectedColors state if we restored a preserved color
          if (preservedColor) {
            this.selectedColors[normalizedHandle] = preservedColor;
          }

          // Function to update color labels with selected color name
          const updateColorLabels = (colorName) => {
            if (colorName) {
              // Capitalize first letter of each word
              const formattedColorName = colorName
                .split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                .join(' ');
              if (isProtectiveTopCoat) {
                colorLabelAbove.textContent = `COLORBOND®: ${formattedColorName}`;
                // colorLabelBelow is static and always shows "Recommended colour: White"
              } else {
                colorLabelAbove.textContent = `Color: ${formattedColorName}`;
              }
            } else if (isProtectiveTopCoat) {
              colorLabelAbove.textContent = 'COLORBOND®: ';
              // colorLabelBelow remains static: "Recommended colour: White" (only for protective top coat)
            } else {
              colorLabelAbove.textContent = 'Color: ';
            }
          };

          // Get initial selected color name
          let initialColorName = '';
          if (selectedColor) {
            // Find the selected input to get the color name
            const selectedInput = Array.from(inputs).find((input) => {
              const inputColor = (input.value || '').toString().trim().toLowerCase();
              const selectedColorNormalized = selectedColor.toString().trim().toLowerCase();
              return inputColor === selectedColorNormalized;
            });
            if (selectedInput) {
              initialColorName = selectedInput.value || selectedInput.getAttribute('title') || selectedColor;
            } else {
              initialColorName = selectedColor;
            }
          } else {
            // Get first available color as default
            const firstInput = inputs[0];
            if (firstInput) {
              initialColorName = firstInput.value || firstInput.getAttribute('title') || '';
            }
          }
          updateColorLabels(initialColorName);

          inputs.forEach((input, index) => {
            const oldId = input.id;
            const newId = `${oldId}-${this.sectionId}-${bd.data.id}`;
            input.id = newId;
            if (labels[index]) {
              labels[index].setAttribute('for', newId);
            }
            // Update name to be unique per calculator
            const dataIndex = input.dataset.index || '';
            input.name = `option-${this.sectionId}-${bd.data.id}-${dataIndex}`;

            // Set checked state based on selectedColor
            if (selectedColor) {
              const inputColor = (input.value || '').toString().trim().toLowerCase();
              const selectedColorNormalized = selectedColor.toString().trim().toLowerCase();
              if (inputColor === selectedColorNormalized) {
                input.checked = true;
              } else {
                input.checked = false;
              }
            }
          });

          // Limit visible swatches to 4 and add "Show more" dropdown
          const swatchesContainer = colorSwatchesContainer.querySelector('.custom-color-swatches__container');
          let dropdown = null; // Declare dropdown in outer scope
          let updateAllSwatchSelection = null; // Declare function in outer scope

          if (swatchesContainer) {
            const allSwatches = Array.from(swatchesContainer.querySelectorAll('.custom-color-swatch'));
            const maxVisible = 4;

            // Create updateAllSwatchSelection function using helper method
            // Note: dropdown will be created later, so we'll update this function after dropdown is created
            updateAllSwatchSelection = this.createUpdateAllSwatchSelectionFunction(swatchesContainer, null);

            if (allSwatches.length > maxVisible) {
              // Hide swatches beyond the first 4
              allSwatches.forEach((swatch, index) => {
                if (index >= maxVisible) {
                  swatch.classList.add('color-swatch-hidden');
                  swatch.style.display = 'none';
                }
              });

              // Create "Show more" button and dropdown
              const showMoreContainer = document.createElement('div');
              showMoreContainer.className = 'color-swatches-show-more-container';

              const showMoreButton = document.createElement('button');
              showMoreButton.type = 'button';
              showMoreButton.className = 'color-swatches-show-more-btn';
              showMoreButton.textContent = 'Show more';
              showMoreButton.setAttribute('aria-expanded', 'false');
              showMoreButton.setAttribute('aria-label', 'Show more color options');

              dropdown = document.createElement('div');
              dropdown.className = 'color-swatches-dropdown';
              dropdown.style.display = 'none';

              // Update the function now that dropdown exists
              updateAllSwatchSelection = this.createUpdateAllSwatchSelectionFunction(swatchesContainer, dropdown);

              // Add hidden swatches to dropdown in list format
              const hiddenSwatches = allSwatches.slice(maxVisible);
              hiddenSwatches.forEach((swatch) => {
                const dropdownRow = document.createElement('div');
                dropdownRow.className = 'color-swatches-dropdown-row';

                const clonedSwatch = swatch.cloneNode(true);
                clonedSwatch.style.display = 'inline-block';
                clonedSwatch.classList.remove('color-swatch-hidden', 'custom-color-swatch--selected');
                // Remove selected class from cloned label as well
                const clonedLabel = clonedSwatch.querySelector('.custom-color-swatch__label');
                if (clonedLabel) {
                  clonedLabel.classList.remove('custom-color-swatch--selected');
                }
                // Uncheck the radio input in the cloned swatch and remove checked attribute
                const clonedInput = clonedSwatch.querySelector('input[type="radio"]');
                if (clonedInput) {
                  clonedInput.checked = false;
                  clonedInput.removeAttribute('checked');
                }

                // Get color name from the swatch's title or data-value
                const colorName = swatch.getAttribute('title') || swatch.dataset.value || '';

                // Ensure cloned swatch also has tooltip (cloneNode should copy it, but ensure it exists)
                const clonedTooltip = clonedSwatch.querySelector('.custom-color-swatch__tooltip');
                if (!clonedTooltip && colorName) {
                  const tooltipElement = document.createElement('div');
                  tooltipElement.className = 'custom-color-swatch__tooltip';
                  tooltipElement.textContent = colorName.toUpperCase().trim();
                  clonedSwatch.appendChild(tooltipElement);
                }

                const colorNameSpan = document.createElement('span');
                colorNameSpan.className = 'color-swatches-dropdown-name';
                colorNameSpan.textContent = colorName;

                dropdownRow.appendChild(clonedSwatch);
                dropdownRow.appendChild(colorNameSpan);

                // Handle row click using helper method
                this.handleDropdownRowClick({
                  dropdownRow,
                  clonedSwatch,
                  swatch,
                  swatchesContainer,
                  dropdown,
                  showMoreButton,
                  productHandle: bd.handle,
                  isProtectiveTopCoat,
                  updateAllSwatchSelection,
                });

                dropdown.appendChild(dropdownRow);
              });

              // Toggle dropdown
              showMoreButton.addEventListener('click', (e) => {
                e.stopPropagation();
                const isExpanded = showMoreButton.getAttribute('aria-expanded') === 'true';
                if (isExpanded) {
                  dropdown.style.display = 'none';
                  showMoreButton.setAttribute('aria-expanded', 'false');
                  showMoreButton.textContent = 'Show more';
                } else {
                  dropdown.style.display = 'block';
                  showMoreButton.setAttribute('aria-expanded', 'true');
                  showMoreButton.textContent = 'Show less';
                }
              });

              // Close dropdown when clicking outside
              document.addEventListener('click', (e) => {
                if (!showMoreContainer.contains(e.target)) {
                  dropdown.style.display = 'none';
                  showMoreButton.setAttribute('aria-expanded', 'false');
                  showMoreButton.textContent = 'Show more';
                }
              });

              showMoreContainer.appendChild(showMoreButton);
              showMoreContainer.appendChild(dropdown);
              swatchesContainer.appendChild(showMoreContainer);
            }

            // Add explicit click handlers for visible swatches to ensure they work
            // This handles cases where label clicks might not properly trigger radio inputs
            // IMPORTANT: This must be outside the dropdown conditional so it works for all products (including those with 4 or fewer colors)
            const visibleSwatches = swatchesContainer.querySelectorAll('.custom-color-swatch');
            visibleSwatches.forEach((swatch) => {
              // Find the input and label within this swatch
              const input = swatch.querySelector('input[type="radio"]');
              const label = swatch.querySelector('label.custom-color-swatch__label');

              if (!input || input.disabled) return;

              // Create CSS-based tooltip similar to LRDIY_LandingPages project
              // Get the color name from title, data-value, or input value
              let tooltipText = swatch.getAttribute('title') || swatch.dataset.value || input.value || '';

              // Format the color name (uppercase like in LRDIY_LandingPages)
              if (tooltipText) {
                tooltipText = tooltipText.toUpperCase().trim();

                // Check if tooltip element already exists
                let tooltipElement = swatch.querySelector('.custom-color-swatch__tooltip');

                if (tooltipElement) {
                  // Update existing tooltip text
                  tooltipElement.textContent = tooltipText;
                } else {
                  // Create tooltip element
                  tooltipElement = document.createElement('div');
                  tooltipElement.className = 'custom-color-swatch__tooltip';
                  tooltipElement.textContent = tooltipText;
                  swatch.appendChild(tooltipElement);
                }

                // Don't set title attribute to prevent native browser tooltip
                // Use data-tooltip instead for our CSS tooltip
                swatch.dataset.tooltip = tooltipText;
                swatch.removeAttribute('title');
                if (label) {
                  label.removeAttribute('title');
                  label.style.pointerEvents = 'auto';
                }
              }

              // Handler function to update selection using helper method
              const handleColorSelection = (e) => {
                this.handleSwatchColorSelection(e, {
                  input,
                  swatch,
                  swatchesContainer,
                  productHandle: bd.handle,
                  isProtectiveTopCoat,
                  isToughCoat,
                  updateColorLabels,
                });
              };

              // Attach handler to both the swatch container and the label
              swatch.addEventListener('click', handleColorSelection, true); // Use capture phase
              if (label) {
                label.addEventListener('click', handleColorSelection, true); // Also on label
              }
            });

            // Listen for selection changes on all inputs - scoped to this product only
            // IMPORTANT: This must be outside the dropdown conditional so it works for all products
            this.setupSwatchChangeListener(
              swatchesContainer,
              bd.handle,
              isProtectiveTopCoat,
              isToughCoat,
              updateAllSwatchSelection,
              updateColorLabels
            );

            // Also listen for custom color swatch events from the snippet's JavaScript
            const swatchesElement = colorSwatchesContainer.querySelector('.custom-color-swatches');
            if (swatchesElement) {
              swatchesElement.addEventListener('custom-color-swatch:changed', (e) => {
                const detail = e.detail;
                if (detail?.value) {
                  const normalizedHandle = this.normalizeHandle(bd.handle);
                  this.selectedColors[normalizedHandle] = detail.value;

                  // Update color labels
                  updateColorLabels(detail.value);

                  // Optimize: Only update variants for this specific product
                  this.updateProductVariants(bd.handle);

                  // Update product and variant images for thermal coating and tough coat when color changes (after variants are updated)
                  const isToughCoat = normalizedHandle === 'tough-coat';
                  if (isProtectiveTopCoat || isToughCoat) {
                    this.updateProductImageForColor(bd.handle, detail.value);
                  }
                }
              });
            }

            // Initial update
            updateAllSwatchSelection();
          }
        }

        // Assemble the wrapper: label above, swatches, label below (if exists)
        colorSwatchesWrapper.appendChild(colorLabelAbove);
        colorSwatchesWrapper.appendChild(colorSwatchesContainer);
        if (colorLabelBelow) {
          colorSwatchesWrapper.appendChild(colorLabelBelow);
        }

        colorSwatchesAndToggleContainer.appendChild(colorSwatchesWrapper);

        // Add full cover toggle for waterproof-sealant product (to the right of color swatches)
        if (isWaterproofSealant && this.showFullCoverToggle) {
          const fullCoverToggleContainer = document.createElement('div');
          fullCoverToggleContainer.className = 'product-full-cover-toggle-container';
          fullCoverToggleContainer.style.display = 'flex';
          fullCoverToggleContainer.style.flexDirection = 'column';
          fullCoverToggleContainer.style.alignItems = 'flex-start';
          fullCoverToggleContainer.style.gap = '0.5rem';

          const toggleLabelWrapper = document.createElement('div');
          toggleLabelWrapper.className = 'product-full-cover-toggle-label-wrapper';
          toggleLabelWrapper.style.display = 'flex';
          toggleLabelWrapper.style.alignItems = 'center';
          toggleLabelWrapper.style.gap = '0.5rem';

          const toggleLabel = document.createElement('span');
          toggleLabel.className = 'product-full-cover-toggle-label';
          toggleLabel.style.fontSize = '1rem';
          toggleLabel.style.fontWeight = '500';
          toggleLabel.style.color = '#000000';
          toggleLabel.textContent = this.fullCoverToggleLabel;

          toggleLabelWrapper.appendChild(toggleLabel);

          if (this.fullCoverTooltipText) {
            const tooltipContainer = document.createElement('div');
            tooltipContainer.className = 'product-full-cover-toggle-tooltip-container';
            tooltipContainer.style.position = 'relative';
            tooltipContainer.style.display = 'inline-flex';

            const tooltipTrigger = document.createElement('button');
            tooltipTrigger.type = 'button';
            tooltipTrigger.className = 'product-full-cover-toggle-tooltip-trigger';
            tooltipTrigger.setAttribute('aria-label', 'Toggle information');
            tooltipTrigger.style.display = 'inline-flex';
            tooltipTrigger.style.alignItems = 'center';
            tooltipTrigger.style.justifyContent = 'center';
            tooltipTrigger.style.width = '1.125rem';
            tooltipTrigger.style.height = '1.125rem';
            tooltipTrigger.style.padding = '0';
            tooltipTrigger.style.backgroundColor = 'transparent';
            tooltipTrigger.style.border = '1px solid #16a34a';
            tooltipTrigger.style.borderRadius = '50%';
            tooltipTrigger.style.color = '#16a34a';
            tooltipTrigger.style.fontSize = '0.75rem';
            tooltipTrigger.style.fontWeight = '700';
            tooltipTrigger.style.lineHeight = '1';
            tooltipTrigger.style.cursor = 'pointer';
            tooltipTrigger.textContent = 'i';

            const tooltip = document.createElement('div');
            tooltip.className = 'product-full-cover-toggle-tooltip';
            tooltip.textContent = this.fullCoverTooltipText;

            tooltipTrigger.addEventListener('click', (e) => {
              e.stopPropagation();
              const isActive = tooltipContainer.classList.contains('active');

              // Close all other tooltips first
              const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
              const allTooltipContainers = document.querySelectorAll(
                `${sectionPrefix} .product-why-tooltip-container, ${sectionPrefix} .product-full-cover-toggle-tooltip-container`
              );
              allTooltipContainers.forEach((container) => {
                if (container !== tooltipContainer) {
                  container.classList.remove('active');
                }
              });

              // Toggle this tooltip
              if (isActive) {
                tooltipContainer.classList.remove('active');
              } else {
                tooltipContainer.classList.add('active');
              }
            });

            tooltipContainer.appendChild(tooltipTrigger);
            tooltipContainer.appendChild(tooltip);
            toggleLabelWrapper.appendChild(tooltipContainer);
          }

          const toggleWrapper = document.createElement('label');
          toggleWrapper.className = 'roof-type-toggle';
          toggleWrapper.style.position = 'relative';
          toggleWrapper.style.display = 'inline-block';
          toggleWrapper.style.width = '4rem';
          toggleWrapper.style.height = '2rem';
          toggleWrapper.style.cursor = 'pointer';
          toggleWrapper.setAttribute('for', `full-cover-toggle-${this.sectionIdOnly}`);

          const toggleInput = document.createElement('input');
          toggleInput.type = 'checkbox';
          toggleInput.id = `full-cover-toggle-${this.sectionIdOnly}`;
          toggleInput.className = 'roof-type-toggle-input';
          toggleInput.setAttribute('aria-label', 'Toggle full cover');
          toggleInput.dataset.fullCover = 'false';
          toggleInput.dataset.calculatorId = this.sectionIdOnly;
          toggleInput.checked = this.fullCover;

          // Set up event listener for the toggle
          toggleInput.addEventListener('change', () => this.handleFullCoverToggle());

          const toggleSlider = document.createElement('span');
          toggleSlider.className = 'roof-type-toggle-slider';

          toggleWrapper.appendChild(toggleInput);
          toggleWrapper.appendChild(toggleSlider);

          fullCoverToggleContainer.appendChild(toggleLabelWrapper);
          fullCoverToggleContainer.appendChild(toggleWrapper);

          colorSwatchesAndToggleContainer.appendChild(fullCoverToggleContainer);
        }

        titleColumn.appendChild(colorSwatchesAndToggleContainer);
      } else if (bd.data.showCustomColorSwatch && bd.data.customColors && bd.data.customColors.length > 0) {
        this.createCustomColorSwatchesSection(bd, titleColumn, preservedSelectedColors);
      }

      cardContent.appendChild(titleColumn);

      // Right column: Variants only
      const variantsColumn = document.createElement('div');
      variantsColumn.className = 'product-card-variants-column';

      const variantsDiv = document.createElement('div');
      variantsDiv.className = 'product-card-variants';

      // If no buckets, show available variants from product data (but not for rapid-cure-spray)
      if (
        bd.buckets.length === 0 &&
        bd.data.variants &&
        bd.data.variants.length > 0 &&
        bd.handle !== 'rapid-cure-spray'
      ) {
        bd.data.variants.forEach((variant) => {
          const variantItem = document.createElement('div');
          variantItem.className = 'variant-item';

          const img = document.createElement('img');
          img.src = variant.imageUrl || bd.data.featured_image || '/placeholder.png';
          img.alt = variant.label || '';
          img.className = 'variant-image';

          const variantInfo = document.createElement('div');
          variantInfo.className = 'variant-info';

          const label = document.createElement('div');
          label.className = 'variant-label';
          label.textContent = variant.label || `${variant.size || ''}L`;

          const price = document.createElement('div');
          price.className = 'variant-price';
          price.textContent = `AUD ${variant.price.toFixed(2)}`;

          variantInfo.appendChild(label);
          variantInfo.appendChild(price);

          const countDiv = document.createElement('div');
          countDiv.className = 'variant-count';

          const decreaseBtn = document.createElement('button');
          decreaseBtn.className = 'variant-count-btn';
          decreaseBtn.textContent = '-';
          decreaseBtn.disabled = true;

          const countInput = document.createElement('input');
          countInput.type = 'number';
          countInput.className = 'variant-count-input';
          countInput.value = 0;
          countInput.min = 0;
          countInput.disabled = true;

          const increaseBtn = document.createElement('button');
          increaseBtn.className = 'variant-count-btn';
          increaseBtn.textContent = '+';
          increaseBtn.disabled = true;

          countDiv.appendChild(decreaseBtn);
          countDiv.appendChild(countInput);
          countDiv.appendChild(increaseBtn);

          variantItem.appendChild(img);
          variantItem.appendChild(variantInfo);
          variantItem.appendChild(countDiv);
          variantsDiv.appendChild(variantItem);
        });
      }

      bd.buckets.forEach((bucket) => {
        const variantItem = document.createElement('div');
        variantItem.className = 'variant-item';

        const img = document.createElement('img');
        img.src = bucket.imageUrl || '/placeholder.png';
        img.alt = bucket.label || '';
        img.className = 'variant-image';

        const variantInfo = document.createElement('div');
        variantInfo.className = 'variant-info';

        const label = document.createElement('div');
        label.className = 'variant-label';
        label.textContent = bucket.label || `${this.formatDecimal(bucket.size)}L`;

        const price = document.createElement('div');
        price.className = 'variant-price';
        price.textContent = `AUD ${bucket.price.toFixed(2)}`;

        variantInfo.appendChild(label);
        variantInfo.appendChild(price);

        const countDiv = document.createElement('div');
        countDiv.className = 'variant-count';

        const countInput = document.createElement('input');
        countInput.type = 'number';
        countInput.className = 'variant-count-input';
        countInput.value = bucket.count;
        countInput.min = 0;

        const decreaseBtn = document.createElement('button');
        decreaseBtn.className = 'variant-count-btn';
        decreaseBtn.textContent = '-';
        decreaseBtn.addEventListener('click', () => {
          if (bucket.count > 0) {
            bucket.count--;
            countInput.value = bucket.count;
            this.updateCalculations();
          }
        });

        countInput.addEventListener('change', (e) => {
          bucket.count = Math.max(0, Number.parseInt(e.target.value, 10) || 0);
          countInput.value = bucket.count;
          this.updateCalculations();
        });

        const increaseBtn = document.createElement('button');
        increaseBtn.className = 'variant-count-btn';
        increaseBtn.textContent = '+';
        increaseBtn.addEventListener('click', () => {
          bucket.count++;
          countInput.value = bucket.count;
          this.updateCalculations();
        });

        countDiv.appendChild(decreaseBtn);
        countDiv.appendChild(countInput);
        countDiv.appendChild(increaseBtn);

        variantItem.appendChild(img);
        variantItem.appendChild(variantInfo);
        variantItem.appendChild(countDiv);
        variantsDiv.appendChild(variantItem);
      });

      // Color picker if available
      const colors = bd.data.colors || [];
      if (colors.length > 0) {
        const colorPicker = document.createElement('div');
        colorPicker.className = 'color-picker';

        colors.forEach((color) => {
          const colorOption = document.createElement('div');
          colorOption.className = 'color-option';
          colorOption.style.backgroundColor = this.getColorHex(color);
          if (this.selectedColors[bd.handle] === color) {
            colorOption.classList.add('selected');
          }
          colorOption.addEventListener('click', () => {
            this.selectedColors[bd.handle] = color;
            this.updateCalculations();
          });
          colorPicker.appendChild(colorOption);
        });

        variantsDiv.appendChild(colorPicker);
      }

      variantsColumn.appendChild(variantsDiv);
      cardContent.appendChild(variantsColumn);
      card.appendChild(cardContent);

      container.appendChild(card);
    });
  }

  getColorHex(colorName) {
    // Basic color mapping - you may need to expand this
    const colorMap = {
      White: '#ffffff',
      Black: '#000000',
      Red: '#ff0000',
      Blue: '#0000ff',
      Green: '#008000',
      Grey: '#808080',
      Gray: '#808080',
    };
    return colorMap[colorName] || '#cccccc';
  }

  updatePricing() {
    const totalPrice = this.breakdowns
      .filter((b) => b.selected && this.isVisible(b.handle))
      .flatMap((b) => b.buckets)
      .reduce((s, b) => s + b.count * b.price, 0);

    this.originalPrice = totalPrice;
    // If discount rate is 0, no discount is applied
    if (this.discountRate > 0) {
      this.discountedPrice = Math.round(totalPrice * (1 - this.discountRate) * 100) / 100;
      this.savings = Math.round((totalPrice - this.discountedPrice) * 100) / 100;
    } else {
      // No discount - use original price
      this.discountedPrice = totalPrice;
      this.savings = 0;
    }

    // Calculate total items from visible selected products
    const visibleItems = this.breakdowns
      .filter((b) => b.selected && this.isVisible(b.handle))
      .reduce((sum, b) => sum + b.buckets.reduce((s, bk) => s + bk.count, 0), 0);

    // Add bonus kit count (always 1 item if area > 0, since bonus kit is always included)
    const area = this.getArea();
    const bonusKitCount = area > 0 ? 1 : 0;

    this.totalItems = visibleItems + bonusKitCount;

    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');

    // Update UI elements
    const totalItemsEl = document.querySelector(`${sectionPrefix} #total-items-${sectionIdOnly}`);
    if (totalItemsEl) totalItemsEl.textContent = this.totalItems;

    const savingsEl = document.querySelector(`${sectionPrefix} #savings-${sectionIdOnly}`);
    if (savingsEl) savingsEl.textContent = this.savings.toFixed(2);

    const originalPriceMobile = document.querySelector(`${sectionPrefix} #original-price-mobile-${sectionIdOnly}`);
    if (originalPriceMobile) originalPriceMobile.textContent = this.originalPrice.toFixed(2);

    const discountedPriceMobile = document.querySelector(`${sectionPrefix} #discounted-price-mobile-${sectionIdOnly}`);
    if (discountedPriceMobile) discountedPriceMobile.textContent = this.discountedPrice.toFixed(2);

    const originalPriceDesktop = document.querySelector(`${sectionPrefix} #original-price-desktop-${sectionIdOnly}`);
    if (originalPriceDesktop) originalPriceDesktop.textContent = this.originalPrice.toFixed(2);

    const discountedPriceDesktop = document.querySelector(
      `${sectionPrefix} #discounted-price-desktop-${sectionIdOnly}`
    );
    if (discountedPriceDesktop) discountedPriceDesktop.textContent = this.discountedPrice.toFixed(2);

    // Check if any product has quantity > 0 (excluding bonus kit)
    // visibleItems already excludes brush-roller and only counts products with count > 0
    const hasProductsWithQuantity = visibleItems > 0;

    const ctaBtn = document.querySelector(`${sectionPrefix} #cta-caravan-${sectionIdOnly}`);
    if (ctaBtn) {
      ctaBtn.disabled = !hasProductsWithQuantity;
    }
  }

  /**
   * Build variant URL for fetching from Shopify API
   * @param {string|number} variantId - The variant ID
   * @returns {string} The variant URL
   */
  buildVariantUrl(variantId) {
    const root = globalThis.Shopify?.routes?.root || '/';
    let variantUrl = `${root}variants/${variantId}.js`.replace('//', '/');
    // If root is just '/', make sure we have the correct format
    if (variantUrl === `/variants/${variantId}.js`) {
      variantUrl = `/variants/${variantId}.js`;
    }
    return variantUrl;
  }

  /**
   * Extract image URL from variant object
   * @param {Object} variant - The variant object from Shopify API
   * @returns {string|null} The image URL or null
   */
  extractImageFromVariant(variant) {
    // Try variant-specific image fields first
    if (variant.featured_image) {
      return variant.featured_image;
    }
    if (variant.image) {
      return variant.image;
    }
    if (variant.featured_media?.preview?.image?.url) {
      return variant.featured_media.preview.image.url;
    }
    if (variant.media && variant.media.length > 0) {
      const firstMedia = variant.media[0];
      if (firstMedia.preview?.image?.url) {
        return firstMedia.preview.image.url;
      }
      if (firstMedia.src) {
        return firstMedia.src;
      }
    }
    return null;
  }

  /**
   * Fetch product image as fallback when variant has no image
   * @param {string|number} productId - The product ID
   * @param {string} root - The Shopify routes root
   * @returns {Promise<string|null>} The image URL or null
   */
  async fetchProductImage(productId, root) {
    try {
      const productUrl = `${root}products/${productId}.js`.replace('//', '/');
      const productResponse = await fetch(productUrl);
      if (!productResponse.ok) {
        return null;
      }
      const product = await productResponse.json();
      const imageUrl =
        product.featured_image || product.featured_media?.preview?.image?.url || product.images?.[0] || null;
      if (imageUrl) {
        console.log('Fetched product image for variant:', imageUrl);
      }
      return imageUrl;
    } catch (e) {
      console.debug('Could not fetch product image for variant:', e);
      return null;
    }
  }

  /**
   * Normalize image URL to ensure proper format
   * @param {string|Object|null} imageUrl - The image URL (may be string, object, or null)
   * @returns {string|null} Normalized image URL or null
   */
  normalizeImageUrl(imageUrl) {
    if (!imageUrl) {
      return null;
    }

    // Handle case where imageUrl might be an object
    if (typeof imageUrl === 'object' && imageUrl !== null) {
      imageUrl = imageUrl.src || imageUrl.url || imageUrl.href || null;
    }

    // Only proceed if we have a valid string value
    if (!imageUrl || typeof imageUrl !== 'string') {
      return null;
    }

    // Normalize URL format
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
      return imageUrl; // Already a full URL
    }
    if (imageUrl.startsWith('//')) {
      return `https:${imageUrl}`; // Protocol-relative URL
    }
    if (!imageUrl.startsWith('/')) {
      return `/${imageUrl}`; // Relative URL without leading slash
    }
    return imageUrl; // Already a proper path
  }

  /**
   * Build the final variant response object
   * @param {Object} variant - The variant object from Shopify API
   * @param {string|null} imageUrl - The normalized image URL
   * @returns {Object} The formatted variant response
   */
  buildVariantResponse(variant, imageUrl) {
    return {
      id: variant.id,
      product_id: variant.product_id,
      title: variant.title,
      price: variant.price / 100,
      image: imageUrl,
      available: variant.available,
      product_title: variant.product_title || '',
      product_handle: variant.product_handle || '',
    };
  }

  // Helper method to fetch variant information from Shopify
  async fetchVariantInfo(variantId) {
    if (!variantId) return null;

    try {
      const variantUrl = this.buildVariantUrl(variantId);
      console.log(
        'Fetching variant from URL:',
        variantUrl,
        'Root:',
        globalThis.Shopify?.routes?.root || '/',
        'Shopify routes:',
        globalThis.Shopify?.routes
      );

      const response = await fetch(variantUrl);
      if (!response.ok) {
        console.warn(
          `Failed to fetch variant ${variantId}: ${response.status} ${response.statusText}`,
          `URL: ${variantUrl}`
        );
        return null;
      }

      const variant = await response.json();
      console.log('Variant fetched successfully:', {
        id: variant.id,
        hasImage: !!(variant.featured_image || variant.image),
      });

      // Extract image from variant
      let imageUrl = this.extractImageFromVariant(variant);

      // Debug: log if no image found in variant
      if (!imageUrl) {
        console.log('No image in variant response, will try product. Variant structure:', {
          id: variant.id,
          has_featured_image: !!variant.featured_image,
          has_image: !!variant.image,
          has_featured_media: !!variant.featured_media,
          has_media: !!variant.media,
          product_id: variant.product_id,
        });
      }

      // If still no image, try to get from product if available
      if (!imageUrl && variant.product_id) {
        const root = globalThis.Shopify?.routes?.root || '/';
        imageUrl = await this.fetchProductImage(variant.product_id, root);
      }

      // Normalize image URL
      imageUrl = this.normalizeImageUrl(imageUrl);

      return this.buildVariantResponse(variant, imageUrl);
    } catch (error) {
      console.error('Failed to fetch variant info:', error, 'Variant ID:', variantId);
      return null;
    }
  }

  /**
   * Determine which bonus product to use based on area threshold
   * @param {number} area - The calculated area
   * @returns {Object} Object with useBrush, handle, and variantId
   */
  determineBonusProduct(area) {
    const useBrush = area < this.bonusKitAreaThreshold;
    return {
      useBrush,
      handle: useBrush ? this.bonusBrushProductHandle : this.bonusBrushRollerProductHandle,
      variantId: useBrush ? this.bonusBrushVariantId : this.bonusBrushRollerVariantId,
    };
  }

  /**
   * Check if there are any products in the kit (excluding bonus kit products)
   * @param {string} bonusBrushHandle - Brush product handle
   * @param {string} bonusBrushRollerHandle - Brush roller product handle
   * @returns {boolean} True if there are products in the kit
   */
  hasProductsInKit(bonusBrushHandle, bonusBrushRollerHandle) {
    return this.breakdowns.some(
      (bd) =>
        bd.handle !== 'brush-roller' &&
        (!bonusBrushHandle || bd.handle !== bonusBrushHandle) &&
        (!bonusBrushRollerHandle || bd.handle !== bonusBrushRollerHandle) &&
        bd.selected &&
        this.isVisible(bd.handle) &&
        bd.buckets?.some((b) => b.count > 0)
    );
  }

  /**
   * Determine if bonus banner should be shown
   * @param {number} area - The calculated area
   * @param {boolean} hasProductsInKit - Whether there are products in the kit
   * @param {boolean} hasBonusConfig - Whether bonus configuration exists
   * @returns {boolean} True if banner should be shown
   */
  shouldShowBonusBanner(area, hasProductsInKit, hasBonusConfig) {
    return area > 0 && hasProductsInKit && hasBonusConfig;
  }

  /**
   * Find image for a product by handle
   * @param {string} handle - Product handle
   * @param {string} fallback - Fallback image path
   * @returns {string} Image URL or fallback
   */
  findImageForProduct(handle, fallback) {
    const product = this.products.find((p) => p.handle === handle);
    if (!product) return fallback;
    const variantWithImage = product.variants?.find((v) => v.imageUrl) || {};
    return variantWithImage.imageUrl || product.featured_image || fallback;
  }

  /**
   * Find bonus kit image from product handle
   * @param {boolean} useBrush - Whether to use brush product
   * @param {string} currentBonusHandle - Current bonus product handle
   * @param {string} currentBonusVariantId - Current bonus variant ID
   * @returns {string|null} Image URL or null
   */
  findBonusKitImageFromProduct(useBrush, currentBonusHandle, currentBonusVariantId) {
    const fallbackImage = useBrush
      ? '/images/products/liquid-rubber-diy-brush-set-caravan-roof.webp'
      : '/images/products/liquid-rubber-diy-brush-roller-set-caravan-roof.webp';
    let bonusKitImage = this.findImageForProduct(currentBonusHandle, fallbackImage);

    // If we have a specific variant ID, try to get its image
    if (currentBonusVariantId) {
      const product = this.products.find((p) => p.handle === currentBonusHandle);
      if (product) {
        const variant = product.variants?.find((v) => String(v.variantId) === String(currentBonusVariantId));
        if (variant?.imageUrl) {
          bonusKitImage = variant.imageUrl;
        }
      }
    }

    return bonusKitImage;
  }

  /**
   * Find bonus kit image from variant ID
   * @param {string} currentBonusVariantId - Current bonus variant ID
   * @returns {Promise<string|null>} Image URL or null
   */
  async findBonusKitImageFromVariantId(currentBonusVariantId) {
    console.log('Fetching variant info for variant ID:', currentBonusVariantId);
    const variantInfo = await this.fetchVariantInfo(currentBonusVariantId);

    if (!variantInfo) {
      console.error(
        'Failed to fetch variant info - fetchVariantInfo returned null for variant ID:',
        currentBonusVariantId
      );
      return null;
    }

    if (variantInfo.image) {
      console.log('Successfully fetched variant/product image:', variantInfo.image);
      return variantInfo.image;
    }

    console.warn('Variant info fetched but no image found. Variant info:', {
      id: variantInfo.id,
      product_id: variantInfo.product_id,
      title: variantInfo.title,
      hasImage: !!variantInfo.image,
    });
    return null;
  }

  /**
   * Find bonus kit image using handle or variant ID
   * @param {boolean} useBrush - Whether to use brush product
   * @param {string} currentBonusHandle - Current bonus product handle
   * @param {string} currentBonusVariantId - Current bonus variant ID
   * @returns {Promise<string|null>} Image URL or null
   */
  async findBonusKitImage(useBrush, currentBonusHandle, currentBonusVariantId) {
    if (currentBonusHandle) {
      return this.findBonusKitImageFromProduct(useBrush, currentBonusHandle, currentBonusVariantId);
    }

    if (currentBonusVariantId) {
      return await this.findBonusKitImageFromVariantId(currentBonusVariantId);
    }

    return null;
  }

  /**
   * Create error handler for image element
   * @param {HTMLElement} imageEl - Image element
   * @param {string} fallbackImage - Fallback image path
   * @returns {Function} Error handler function
   */
  createImageErrorHandler(imageEl, fallbackImage) {
    let errorHandlerCalled = false;
    return function () {
      if (errorHandlerCalled) return;
      errorHandlerCalled = true;

      const currentSrcPath = new URL(this.src, globalThis.location.origin).pathname;
      const fallbackPath = fallbackImage;
      if (currentSrcPath !== fallbackPath) {
        this.src = fallbackImage;
      }
      this.onerror = null;
    };
  }

  /**
   * Update bonus kit image element
   * @param {HTMLElement} imageEl - Image element to update
   * @param {string|null} bonusKitImage - Image URL or null
   * @param {string} bonusKitText - Alt text for image
   * @param {boolean} useBrush - Whether to use brush product
   */
  updateBonusKitImage(imageEl, bonusKitImage, bonusKitText, useBrush) {
    imageEl.onerror = null;

    if (!bonusKitImage) {
      imageEl.style.display = 'none';
      imageEl.alt = bonusKitText;
      return;
    }

    const imageUrl = this.normalizeImageUrl(bonusKitImage);
    const currentSrc = imageEl.src.split('?')[0];
    const newSrc = imageUrl.split('?')[0];

    if (currentSrc !== newSrc && !currentSrc.endsWith(newSrc) && !newSrc.endsWith(currentSrc)) {
      imageEl.src = imageUrl;
      imageEl.alt = bonusKitText;

      const fallbackImage = useBrush
        ? '/images/products/liquid-rubber-diy-brush-set-caravan-roof.webp'
        : '/images/products/liquid-rubber-diy-brush-roller-set-caravan-roof.webp';
      imageEl.onerror = this.createImageErrorHandler(imageEl, fallbackImage);
    } else {
      imageEl.alt = bonusKitText;
    }
  }

  /**
   * Update all bonus kit UI elements
   * @param {string} sectionPrefix - Section prefix selector
   * @param {string} sectionIdOnly - Section ID only
   * @param {string} bonusKitValue - Bonus kit value text
   * @param {string} bonusKitText - Bonus kit text
   * @param {string|null} bonusKitImage - Bonus kit image URL
   * @param {boolean} useBrush - Whether to use brush product
   * @param {boolean} shouldShowBonus - Whether to show bonus banner
   */
  updateBonusKitUI(
    sectionPrefix,
    sectionIdOnly,
    bonusKitValue,
    bonusKitText,
    bonusKitImage,
    useBrush,
    shouldShowBonus
  ) {
    const valueEl = document.querySelector(`${sectionPrefix} #bonus-kit-value-${sectionIdOnly}`);
    if (valueEl) valueEl.textContent = bonusKitValue;

    const textEl = document.querySelector(`${sectionPrefix} #bonus-kit-text-${sectionIdOnly}`);
    if (textEl) textEl.textContent = bonusKitText;

    const imageEl = document.querySelector(`${sectionPrefix} #bonus-kit-image-${sectionIdOnly}`);
    if (imageEl) {
      this.updateBonusKitImage(imageEl, bonusKitImage, bonusKitText, useBrush);
    }

    const bannerEl = document.querySelector(`${sectionPrefix} #bonus-kit-banner-${sectionIdOnly}`);
    if (bannerEl) {
      bannerEl.style.display = shouldShowBonus ? '' : 'none';
    }
  }

  async updateBonusKit(area) {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');

    const bonusProduct = this.determineBonusProduct(area);
    const { useBrush, handle: currentBonusHandle, variantId: currentBonusVariantId } = bonusProduct;

    const hasBonusConfig = currentBonusHandle || currentBonusVariantId;
    const hasProductsInKit = this.hasProductsInKit(this.bonusBrushProductHandle, this.bonusBrushRollerProductHandle);
    const shouldShowBonus = this.shouldShowBonusBanner(area, hasProductsInKit, hasBonusConfig);

    const bonusKitValue = useBrush ? this.bonusBrushPrice : this.bonusBrushRollerPrice;
    const bonusKitText = useBrush ? this.bonusBrushText : this.bonusBrushRollerText;

    const bonusKitImage = await this.findBonusKitImage(useBrush, currentBonusHandle, currentBonusVariantId);

    this.updateBonusKitUI(
      sectionPrefix,
      sectionIdOnly,
      bonusKitValue,
      bonusKitText,
      bonusKitImage,
      useBrush,
      shouldShowBonus
    );
  }

  buildCartParam() {
    return this.breakdowns
      .filter((b) => b.selected && (this.isVisible(b.handle) || b.handle === 'brush-roller'))
      .flatMap((b) =>
        b.buckets
          .filter((bk) => bk.count > 0 && bk.variantId)
          .map((bk) => {
            // variantId might be a number or string, handle both cases
            const variantId = typeof bk.variantId === 'string' ? bk.variantId.split('/').pop() : String(bk.variantId);
            return `${variantId}:${bk.count}`;
          })
      )
      .join(',');
  }

  /**
   * Generates the cart payload (variant IDs and quantities) from the
   * calculated breakdowns, including the bonus product.
   * @returns {Array<{id: string|number, quantity: number}>}
   */
  /**
   * Validate that there are items to add to cart
   */
  validateCartItems() {
    const visibleItems = this.breakdowns
      .filter((b) => b.selected && this.isVisible(b.handle))
      .reduce((sum, b) => sum + b.buckets.reduce((s, bk) => s + bk.count, 0), 0);

    if (visibleItems === 0) {
      this.showPopup('Please add at least 1 product to your kit');
      return false;
    }
    return true;
  }

  /**
   * Get cart elements (drawer or notification)
   */
  getCartElements() {
    const cartDrawer = document.querySelector('cart-drawer');
    const cartNotification = document.querySelector('cart-notification');
    return { cartDrawer, cartNotification };
  }

  /**
   * Get CTA button element
   */
  getCtaButton() {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    return document.querySelector(`${sectionPrefix} #cta-caravan-${sectionIdOnly}`);
  }

  /**
   * Set button loading state
   */
  setButtonLoading(ctaBtn, isLoading) {
    if (!ctaBtn) return;
    const spinner = ctaBtn.querySelector('.loading__spinner');
    ctaBtn.disabled = isLoading;
    if (isLoading) {
      ctaBtn.classList.add('loading');
      if (spinner) {
        spinner.classList.remove('hidden');
      }
    } else {
      ctaBtn.classList.remove('loading');
      if (spinner) {
        spinner.classList.add('hidden');
      }
    }
  }

  /**
   * Build FormData for adding an item to cart
   */
  buildCartItemFormData(item, isLastItem, cartElement) {
    const formData = new FormData();
    formData.append('id', item.id);
    formData.append('quantity', item.quantity);

    if (item.properties) {
      Object.entries(item.properties).forEach(([key, value]) => {
        formData.append(`properties[${key}]`, value);
      });
    }

    if (isLastItem && cartElement) {
      formData.append(
        'sections',
        cartElement
          .getSectionsToRender()
          .map((section) => section.id)
          .join(',')
      );
      formData.append('sections_url', globalThis.location.pathname);
    }

    return formData;
  }

  /**
   * Add a single item to cart
   */
  async addItemToCart(item, isLastItem, cartElement) {
    const config = typeof fetchConfig === 'undefined' ? this.getFetchConfig() : fetchConfig('javascript');
    config.headers = config.headers || {};
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = this.buildCartItemFormData(item, isLastItem, cartElement);
    config.body = formData;

    const cartAddUrl = routes?.cart_add_url || globalThis.routes?.cart_add_url || '/cart/add.js';
    const response = await fetch(cartAddUrl, config);

    if (!response.ok) {
      const text = await response.text();
      console.error('Cart add request failed:', {
        status: response.status,
        statusText: response.statusText,
        url: cartAddUrl,
        preview: text.substring(0, 200),
      });
      throw new Error('Error adding item to cart. Please try again.');
    }

    const addResponse = await response.json();

    if (addResponse.status) {
      if (globalThis.PUB_SUB_EVENTS && typeof globalThis.publish === 'function') {
        globalThis.publish(globalThis.PUB_SUB_EVENTS.cartError, {
          source: 'caravan-calculator',
          productVariantId: item.id,
          errors: addResponse.errors || addResponse.description,
          message: addResponse.message,
        });
      }
      throw new Error(addResponse.description || 'Error adding item to cart');
    }

    return addResponse;
  }

  /**
   * Add all items to cart sequentially
   */
  async addAllItemsToCart(itemsToAdd, cartElement) {
    let lastAddResponse = null;

    for (let i = 0; i < itemsToAdd.length; i++) {
      const item = itemsToAdd[i];
      const isLastItem = i === itemsToAdd.length - 1;
      const addResponse = await this.addItemToCart(item, isLastItem, cartElement);

      if (isLastItem) {
        lastAddResponse = addResponse;
      }
    }

    if (!lastAddResponse?.sections) {
      throw new Error('Error loading cart. Please try again.');
    }

    return lastAddResponse;
  }

  /**
   * Verify cart drawer response contains items
   */
  verifyCartDrawerResponse(lastAddResponse) {
    if (!lastAddResponse.sections['cart-drawer']) {
      return false;
    }

    const verifyDoc = new DOMParser().parseFromString(lastAddResponse.sections['cart-drawer'], 'text/html');
    const verifyItems = verifyDoc.querySelectorAll('.cart-item, [id^="CartDrawer-Item"]');
    const cartDrawerElement = verifyDoc.querySelector('#CartDrawer');

    console.log('Sections verification:', {
      itemsCount: verifyItems.length,
      hasCartDrawer: !!cartDrawerElement,
      sectionKeys: Object.keys(lastAddResponse.sections),
      sectionLength: lastAddResponse.sections['cart-drawer']?.length,
    });

    if (verifyItems.length === 0) {
      console.warn('Cart drawer section has no items - this may cause empty cart display');
      return false;
    }

    return true;
  }

  /**
   * Publish cart update event
   */
  async publishCartUpdateEvent(itemsToAdd, lastAddResponse) {
    if (!globalThis.PUB_SUB_EVENTS || typeof globalThis.publish !== 'function') {
      return;
    }

    const startMarker = globalThis.CartPerformance?.createStartingMarker?.('add:wait-for-subscribers');
    await globalThis.publish(globalThis.PUB_SUB_EVENTS.cartUpdate, {
      source: 'caravan-calculator',
      productVariantId: itemsToAdd.at(-1)?.id,
      cartData: lastAddResponse,
    });

    if (startMarker) {
      globalThis.CartPerformance?.measureFromMarker?.('add:wait-for-subscribers', startMarker);
    }
  }

  /**
   * Render cart drawer
   */
  renderCartDrawer(cartDrawer, lastAddResponse) {
    if (!lastAddResponse.sections['cart-drawer']) {
      throw new Error('Error loading cart. Please try again.');
    }

    if (globalThis.CartPerformance?.measure) {
      globalThis.CartPerformance.measure('add:paint-updated-sections', () => {
        cartDrawer.renderContents(lastAddResponse);
      });
    } else {
      cartDrawer.renderContents(lastAddResponse);
    }
  }

  /**
   * Render cart notification
   */
  renderCartNotification(cartNotification, lastAddResponse) {
    const requiredSections = ['cart-notification-product', 'cart-notification-button', 'cart-icon-bubble'];
    const missingSections = requiredSections.filter((id) => !lastAddResponse.sections[id]);

    if (missingSections.length > 0) {
      console.warn('Missing required sections for cart notification:', missingSections);
      throw new Error('Error loading cart notification. Please try again.');
    }

    if (globalThis.CartPerformance?.measure) {
      globalThis.CartPerformance.measure('add:paint-updated-sections', () => {
        cartNotification.renderContents(lastAddResponse);
      });
    } else {
      cartNotification.renderContents(lastAddResponse);
    }
  }

  /**
   * Handle fallback to old cart URL method
   */
  handleCartFallback() {
    const cartParam = this.buildCartParam();
    const cartUrl = `https://${this.storeDomain}/cart/${cartParam}`;
    globalThis.location.href = cartUrl;
  }

  /**
   * Add a bucket to the items array if it has valid variant ID and quantity
   * @param {Object} bucket - The bucket to add
   * @param {Array} items - The items array to add to
   * @param {Object} bd - Breakdown data (for custom color property)
   */
  addBucketToItems(bucket, items, bd) {
    if (!bucket.variantId || bucket.count <= 0) return;

    const id = typeof bucket.variantId === 'string' ? bucket.variantId : String(bucket.variantId);
    const item = { id, quantity: bucket.count };

    if (bd?.data?.showCustomColorSwatch) {
      const normalizedHandle = this.normalizeHandle(bd.handle);
      const selectedColor = this.selectedColors[normalizedHandle];
      if (selectedColor) {
        item.properties = { Color: selectedColor };
      }
    }

    items.push(item);
  }

  /**
   * Process buckets from a breakdown and add them to items
   * @param {Object} bd - The breakdown to process
   * @param {Array} items - The items array to add to
   */
  processBreakdownBuckets(bd, items) {
    if (!bd.buckets || bd.buckets.length === 0) return;

    bd.buckets.forEach((bucket) => {
      this.addBucketToItems(bucket, items, bd);
    });
  }

  /**
   * Check if brush-roller was already added from breakdowns
   * @returns {boolean} True if brush-roller is already in breakdowns with count > 0
   */
  isBrushRollerAdded() {
    return this.breakdowns.some((bd) => bd.handle === 'brush-roller' && bd.buckets?.some((b) => b.count > 0));
  }

  /**
   * Get the variant ID for the bonus product based on area
   * @param {number} area - The calculated area
   * @returns {string|null} The variant ID to add, or null if not available
   */
  getBonusVariantId(area) {
    const useBrush = area < this.bonusKitAreaThreshold;
    const currentBonusHandle = useBrush ? this.bonusBrushProductHandle : this.bonusBrushRollerProductHandle;
    const currentBonusVariantId = useBrush ? this.bonusBrushVariantId : this.bonusBrushRollerVariantId;

    // If variant ID is directly configured, use it
    if (currentBonusVariantId) {
      return String(currentBonusVariantId);
    }

    // Otherwise, find variant from product handle
    if (currentBonusHandle) {
      const bonusProduct = this.products.find((p) => p.handle === currentBonusHandle);
      if (bonusProduct?.variants?.length > 0) {
        const bonusVariant = bonusProduct.variants[0];
        if (bonusVariant?.variantId) {
          return typeof bonusVariant.variantId === 'string' ? bonusVariant.variantId : String(bonusVariant.variantId);
        }
      }
    }

    return null;
  }

  /**
   * Add bonus product to items if conditions are met
   * @param {number} area - The calculated area
   * @param {Array} items - The items array to add to
   */
  addBonusProductIfNeeded(area, items) {
    if (area <= 0 || this.isBrushRollerAdded()) return;

    const variantIdToAdd = this.getBonusVariantId(area);
    if (variantIdToAdd) {
      items.push({
        id: variantIdToAdd,
        quantity: 1, // Always add 1 bonus kit
      });
    }
  }

  getCartItems() {
    const items = [];
    const area = this.getArea();

    // 1. Process main product breakdowns (sealant, coating, primer, geo-textile, spray, etc.)
    this.breakdowns.forEach((bd) => {
      // Include visible products and brush-roller (if it's in breakdowns)
      if (!this.isVisible(bd.handle) && bd.handle !== 'brush-roller') return;

      // Process buckets for this breakdown
      this.processBreakdownBuckets(bd, items);
    });

    // 2. Add the BONUS product if area threshold is met and brush-roller not already added
    this.addBonusProductIfNeeded(area, items);

    return items;
  }

  async goToCheckout() {
    if (!this.validateCartItems()) {
      return;
    }

    const { cartDrawer, cartNotification } = this.getCartElements();

    if (!cartDrawer && !cartNotification) {
      this.handleCartFallback();
      return;
    }

    const ctaBtn = this.getCtaButton();
    this.setButtonLoading(ctaBtn, true);

    try {
      const itemsToAdd = this.getCartItems();

      if (itemsToAdd.length === 0) {
        this.showPopup('Please select at least 1 product');
        this.setButtonLoading(ctaBtn, false);
        return;
      }

      const cartElement = cartDrawer || cartNotification;
      if (cartElement) {
        cartElement.setActiveElement(ctaBtn || document.activeElement);
      }

      const lastAddResponse = await this.addAllItemsToCart(itemsToAdd, cartElement);

      // Update cart note with calculator context (length/area + source URL)
      await this.updateCartNoteFromCalculator();

      if (cartDrawer && !this.verifyCartDrawerResponse(lastAddResponse)) {
        this.showPopup('Error: No items found in cart. Please try again.');
        this.setButtonLoading(ctaBtn, false);
        return;
      }

      await this.publishCartUpdateEvent(itemsToAdd, lastAddResponse);

      if (cartDrawer) {
        this.renderCartDrawer(cartDrawer, lastAddResponse);
      } else if (cartNotification) {
        this.renderCartNotification(cartNotification, lastAddResponse);
      }
    } catch (error) {
      console.error('Error adding items to cart:', error);
      this.showPopup(error.message || 'Error adding items to cart. Please try again.');
    } finally {
      this.setButtonLoading(ctaBtn, false);
      cartDrawer?.classList.remove('is-empty');
    }
  }

  getFetchConfig() {
    // Use fetchConfig from global.js if available, otherwise use a compatible config
    if (typeof fetchConfig !== 'undefined') {
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      // Delete Content-Type when using FormData - browser will set it with boundary
      delete config.headers['Content-Type'];
      return config;
    }
    return {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    };
  }

  showPopup(message) {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const popup = document.querySelector(`${sectionPrefix} #popup-modal-${sectionIdOnly}`);
    const messageEl = document.querySelector(`${sectionPrefix} #popup-message-${sectionIdOnly}`);

    if (popup && messageEl) {
      messageEl.textContent = message;
      popup.classList.remove('hidden');
    }
  }

  closePopup() {
    const sectionPrefix = this.sectionId.startsWith('#') ? this.sectionId : `#${this.sectionId}`;
    const sectionIdOnly =
      this.calculatorId || this.sectionIdOnly || this.sectionId.replace('shopify-section-', '').replace('#', '');
    const popup = document.querySelector(`${sectionPrefix} #popup-modal-${sectionIdOnly}`);
    if (popup) {
      popup.classList.add('hidden');
    }
  }

  openQuickAddModal(productHandle, productId, openerElement) {
    // Construct product URL
    const productUrl = `/products/${productHandle}`;

    // Find or create quick-add-modal element
    let modal = document.querySelector(`#QuickAdd-${productId}`);

    if (!modal) {
      // Try to clone an existing modal structure first
      const existingModal = document.querySelector('quick-add-modal');
      if (existingModal) {
        modal = existingModal.cloneNode(true);
        modal.id = `QuickAdd-${productId}`;
        // Update the close button ID
        const closeBtn = modal.querySelector('[id^="ModalClose-"]');
        if (closeBtn) {
          closeBtn.id = `ModalClose-${productId}`;
        }
        // Update the info div ID
        const infoDiv = modal.querySelector('[id^="QuickAddInfo-"]');
        if (infoDiv) {
          infoDiv.id = `QuickAddInfo-${productId}`;
          infoDiv.innerHTML = ''; // Clear any existing content
        }
        document.body.appendChild(modal);
      } else {
        // No existing modal to clone - create from scratch
        // Get close icon HTML
        const closeIconHTML =
          '<span class="svg-wrapper"><svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><path d="M10 8.586L2.929 1.515 1.515 2.929 8.586 10l-7.071 7.071 1.414 1.414L10 11.414l7.071 7.071 1.414-1.414L11.414 10l7.071-7.071-1.414-1.414L10 8.586z" fill="currentColor"/></svg></span>';

        // Create using insertAdjacentHTML to avoid constructor timing issues
        const tempDiv = document.createElement('div');
        tempDiv.insertAdjacentHTML(
          'beforeend',
          `
          <quick-add-modal id="QuickAdd-${productId}" class="quick-add-modal">
            <div
              role="dialog"
              aria-label="Choose options for product"
              aria-modal="true"
              class="quick-add-modal__content global-settings-popup"
              tabindex="-1"
            >
              <button
                id="ModalClose-${productId}"
                type="button"
                class="quick-add-modal__toggle"
                aria-label="Close"
              >
                ${closeIconHTML}
              </button>
              <div id="QuickAddInfo-${productId}" class="quick-add-modal__content-info"></div>
            </div>
          </quick-add-modal>
        `
        );
        modal = tempDiv.querySelector('quick-add-modal');
        document.body.appendChild(modal);
      }
    }

    // Ensure opener element has data-product-url
    if (!openerElement.dataset.productUrl) {
      openerElement.dataset.productUrl = productUrl;
    }

    // Open the modal - openerElement should be a button with loading spinner
    if (modal && typeof modal.show === 'function') {
      modal.show(openerElement);
    } else {
      console.error('Quick-add modal not found or show method not available');
    }
  }
}
